import type { Dictionary } from "@/lib/i18n/dictionaries/en";

const it: Dictionary = {
  // ---- /citation-styles/alphabetical/<letter> ----
  styleDirectory: {
    notFoundTitle: "Directory degli stili di citazione non trovata",
    metaTitle: "Stili di citazione che iniziano con {label}",
    metaDescription:
      "Sfoglia {count} stili Citation Style Language che iniziano con {label}. Apri uno stile per vedere esempi reali di citazione e un generatore gratuito.",
    ogSubtitle: "{count} stili con esempi e generatori dedicati.",
    ogKind: "Directory A–Z",
    schemaName: "Stili di citazione che iniziano con {label}",
    schemaDescription:
      "Directory alfabetica di {count} stili Citation Style Language che iniziano con {label}.",
    breadcrumbStyles: "Stili di citazione",
    heading: "Stili di citazione che iniziano con {label}",
    intro:
      "Sfoglia tutti i {count} stili di questa parte del catalogo CSL. Ogni pagina include gli esempi formattati dello stile e un generatore di citazioni gratuito.",
    navLabel: "Stili di citazione dalla A alla Z",
    listHeading: "Tutti gli stili che iniziano con {label}",
  },
  nav: {
    tools: "Strumenti",
    guides: "Guide",
    styles: "Tutti gli stili",
    bibliography: "La mia bibliografia",
    search: "Cerca",
    glossary: "Glossario",
    home: "Home",
    menu: "Menu",
  },

  home: {
    title: "Generatore di citazioni gratuito",
    subtitle: "Aggiornato al 2026",
    intro:
      "Incolla un DOI, un URL o una voce BibTeX — oppure cerca un libro — e ottieni una citazione formattata correttamente in APA, MLA, Chicago, Vancouver, Harvard e migliaia di altri stili. Completamente gratuito, senza abbonamento a pagamento e senza registrazione.",
    browseAllStyles: "Sfoglia tutti i {count} stili di citazione →",
    featuresHeading: "Tutto ciò che serve per citare",
    featureStyles: "{count} stili",
    featureStylesDetail: "APA, MLA, Chicago e altri",
    featureBibliographies: "Più bibliografie",
    featureBibliographiesDetail: "Organizzale per progetto",
    featureBookSearch: "Ricerca libri",
    featureBookSearchDetail: "Per titolo o autore",
    featureExport: "Esporta ovunque",
    featureExportDetail: "BibTeX, Word, RIS, CSV",
    featureDarkMode: "Modalità scura",
    featureDarkModeDetail: "Riposante per gli occhi",
    featureNoSignup: "Senza registrazione",
    featureNoSignupDetail: "Gratis per sempre, senza abbonamento a pagamento",
    aboutHeading:
      "Un generatore di citazioni gratuito per studenti e ricercatori",
    aboutBody:
      "CitationEasy supporta oltre 10.000 stili di citazione accademici. Che ti serva APA per psicologia, MLA per letteratura, Chicago per storia o Vancouver per le riviste mediche, la formattazione proviene dalla definizione ufficiale dello stile stesso, non da un'approssimazione.",
    howItWorksHeading: "Come funziona",
    howItWorksStep1:
      "Incolla un DOI, un URL, un ISBN, un identificativo PubMed o arXiv, o una voce BibTeX; puoi anche cercare un libro per titolo.",
    howItWorksStep2:
      "Scegli lo stile di citazione. Digita per cercare tra tutti i {count} disponibili.",
    howItWorksStep3:
      "Copia il riferimento finito, oppure salvalo in una bibliografia ed esportalo in Word, BibTeX o RIS.",
    privacyNote:
      "La tua bibliografia è salvata nel tuo browser. Nessun account e nessun caricamento su server.",
  },

  hubs: {
    toolsTitle: "Strumenti gratuiti per scrivere e citare",
    toolsIntro:
      "{count} strumenti che funzionano nel browser per scrivere e citare: generatori per ogni tipo di fonte, contatori di parole e caratteri, analisi della leggibilità, formattatori e convertitori. Tutti gratuiti e immediati.",
    stylesTitle: "Sfoglia tutti gli stili di citazione",
    stylesIntro:
      "Tutti i {count} stili del progetto Citation Style Language, con ricerca e filtri per paese, materia e disciplina accademica.",
    guidesTitle: "Guide alla citazione e alla scrittura accademica",
    guidesIntro:
      "Guide approfondite su come citare ogni tipo di fonte, valutare ciò che trovi e scrivere meglio.",
    howToCiteTitle: "Come citare qualsiasi fonte",
    howToCiteIntro:
      "Pagine passo passo che abbinano ogni tipo di fonte comune ai principali stili di citazione, ciascuna con un esempio concreto.",
    faqTitle: "Domande frequenti sulle citazioni",
    faqIntro:
      "Risposte brevi e precise alle domande che gli studenti si pongono davvero quando citano.",
    glossaryTitle: "Glossario delle citazioni",
    glossaryIntro:
      "Definizioni chiare dei termini che compaiono nei manuali di stile e nelle consegne d'esame.",
    openHub: "Apri",
  },

  styleLanding: {
    metaTitle: "Generatore di citazioni {style} gratuito",
    metaDescription:
      "Genera citazioni {style} gratis. Incolla un DOI, un URL, un ISBN o BibTeX e ottieni il riferimento e la citazione nel testo nel formato {style} corretto.",
    heading: "Generatore di citazioni {style}",
    intro:
      "Crea citazioni {style} accurate per libri, articoli di rivista e siti web con un clic. Gratuito e senza registrazione.",
    aboutHeading: "Informazioni su {style}",
    whenToUseHeading: "Quando usare {style}",
    factsHeading: "{style} in breve",
    factEdition: "Edizione",
    factSystem: "Sistema di citazione",
    factInText: "Formato nel testo",
    factReferenceList: "Nome dell'elenco",
    factFields: "Comune in",
    systemAuthorDate: "Autore–data",
    systemNumbered: "Numerico",
    systemFootnotes: "Note a piè di pagina",
    howToHeading: "Come usare questo generatore {style}",
    howToStep1:
      "Incolla un URL, un DOI, un ISBN o una voce BibTeX nel campo qui sopra, oppure cerca un libro per titolo.",
    howToStep2:
      "{style} è già selezionato in questa pagina, quindi puoi generare subito.",
    howToStep3:
      "Copia il risultato nel tuo documento, oppure salvalo ed esporta l'elenco completo più tardi.",
    relatedHeading: "Altre risorse su {style}",
    englishOnlyNote:
      "Le guide dettagliate su {style} e gli esempi per tipo di fonte sono disponibili in inglese.",
  },

  // The per-style pages at `/<locale>/citation-style/<slug>` — one per catalog style.
  //
  // The formatted examples the page is built around (the style's own output for a
  // fixed journal article, book and website) and the style's own name stay in
  // `src/data/citation-styles.ts` and `src/data/style-examples-generated.json`. They
  // are language-neutral: a citation rendered by the style's own CSL definition looks
  // the same whatever language the reader speaks, and "translating" one would make the
  // page wrong.
  //
  // The rest of what `src/data/**` supplies to these pages is NOT like that — see the
  // `generic*`, `discipline*` and `internationalLabel` keys below.
  citationStylePage: {
    notFoundTitle: "Stile di citazione non trovato",
    metaTitle: "Generatore di citazioni {style} - Formato {style} gratuito",
    metaFormatAndFields: "{style} è uno stile {format}, usato in {fields}.",
    metaFormatOnly: "{style} è uno stile {format}.",
    metaFieldsOnly: "{style} è usato in {fields}.",
    metaPromiseNote:
      "Guarda una vera nota a piè di pagina in {style} e genera gratis la tua.",
    metaPromiseExamples:
      "Guarda veri esempi di citazioni in {style} per un articolo di rivista, un libro e un sito web, e genera gratis la tua.",
    ogKindFormat: "Stile {format}",
    ogKindDefault: "Stile di citazione",
    // Joins the style's discipline list: "psychology, education and nursing".
    disciplineConjunction: "e",
    // ---- Data-derived facts that are nonetheless mail-merged prose ----
    //
    // Every entry in `src/data/citation-styles-generated.ts` carries exactly the
    // two sentences below with their own name spliced in, exactly one region value
    // ("International" — that is `internationalLabel` above, reused rather than
    // duplicated), and discipline tokens drawn from a closed 26-word vocabulary. None
    // of that is a citation, so none of it is language-neutral the way the formatted
    // examples are. Left in `src/data/**` it renders English facts inside translated
    // chrome, which is precisely the hybrid page this tree exists to avoid.
    //
    // The page substitutes the two templates ONLY when a style's stored text is
    // byte-identical to the English template with its own name interpolated, so the
    // ~100 hand-curated styles keep their bespoke English descriptions instead of
    // being flattened into a generic sentence. `{name}` is the style's full name.
    genericDescription:
      "Stile di citazione per {name}. Dal repository di Citation Style Language.",
    genericWhenUsed:
      "Quando si invia un elaborato a pubblicazioni che utilizzano lo stile {name}, o quando lo si cita in tali pubblicazioni.",

    // The 26 discipline tokens `style-examples-generated.json` uses. Each English
    // value is the exact string the page printed before these keys existed — the raw
    // token with hyphens turned into spaces — so `generic-base` reads "generic base"
    // and `political_science` keeps its underscore. An unknown token (a new one from
    // upstream CSL) falls back to that same prettified form rather than to a key name.
    disciplineAnthropology: "antropologia",
    disciplineAstronomy: "astronomia",
    disciplineBiology: "biologia",
    disciplineBotany: "botanica",
    disciplineChemistry: "chimica",
    disciplineCommunications: "comunicazione",
    disciplineEngineering: "ingegneria",
    disciplineGenericBase: "generale",
    disciplineGeography: "geografia",
    disciplineGeology: "geologia",
    disciplineHistory: "storia",
    disciplineHumanities: "scienze umane",
    disciplineLaw: "diritto",
    disciplineLinguistics: "linguistica",
    disciplineLiterature: "letteratura",
    disciplineMath: "matematica",
    disciplineMedicine: "medicina",
    disciplinePhilosophy: "filosofia",
    disciplinePhysics: "fisica",
    disciplinePoliticalScience: "scienze politiche",
    disciplinePsychology: "psicologia",
    disciplineScience: "scienza",
    disciplineSocialScience: "scienze sociali",
    disciplineSociology: "sociologia",
    disciplineTheology: "teologia",
    disciplineZoology: "zoologia",

    // Plain-language descriptions of the citation-format classes the CSL definitions
    // declare. `{format}` in the fallback is the raw class name from the data.
    formatClassAuthorDate:
      "uno stile autore–data: le citazioni nel testo indicano l'autore e l'anno, e l'elenco dei riferimenti è ordinato alfabeticamente per autore",
    formatClassNumeric:
      "uno stile numerico: le fonti sono numerate nell'ordine in cui vengono citate per la prima volta, e le citazioni nel testo usano questi numeri",
    formatClassNote:
      "uno stile basato su note: le citazioni compaiono in note a piè di pagina o note di chiusura anziché tra parentesi nel testo",
    formatClassLabel:
      "uno stile a etichetta: le citazioni nel testo usano un'etichetta compatta costruita dal nome dell'autore e dall'anno",
    formatClassAuthor:
      "uno stile basato solo sull'autore: le citazioni nel testo indicano l'autore, con i dettagli completi nell'elenco dei riferimenti",
    formatClassOther: "uno stile {format}",
    breadcrumbStyles: "Stili di citazione",
    internationalLabel: "Internazionale",
    heading: "Generatore di citazioni {style}",
    intro:
      "Crea citazioni {style} accurate per libri, articoli e siti web con un clic.",
    examplesHeading: "Esempi di citazioni {style}",
    examplesIntro: "Le stesse tre fonti formattate in {style}:",
    examplesIntroWithFormat:
      "Le stesse tre fonti formattate in {style} — {format}:",
    examplesNoteOnlyIntro:
      "{style} è uno stile basato su note: non prevede un elenco dei riferimenti, e ogni citazione è una nota a piè di pagina. Ecco il nostro esempio di articolo di rivista citato in {style}:",
    labelJournalArticle: "Articolo di rivista",
    labelBook: "Libro",
    labelWebsite: "Sito web",
    labelFootnote: "Nota a piè di pagina (articolo di rivista)",
    labelInText: "Citazione nel testo (per l'articolo di rivista)",
    aboutHeading: "Informazioni su {style}",
    whenToUseLabel: "Quando usare {style}:",
    commonInLabel: "Comune in:",
    subjectAreasLabel: "Aree disciplinari:",
    formattingRulesLabel: "Regole di formattazione:",
    parentStylePrefix: "{style} segue la formattazione di",
    parentStyleSuffix: ", lo stile principale a cui si ispira.",
    howToHeading: "Come usare questo generatore {style}",
    howToBody:
      'Incolla un DOI, l\'URL di un articolo di cronaca o di una pagina web, oppure del BibTeX grezzo nel campo qui sopra. Puoi anche cercare un libro per titolo o autore e citarlo tramite ISBN. Scegli "{style}" nel menu a tendina degli stili (è già preselezionato in questa pagina), poi fai clic su "Genera citazione". Copia il risultato nel tuo elenco dei riferimenti o nella bibliografia. Puoi modificare la voce generata (ad esempio correggere un titolo o aggiungere una data) prima di copiarla.',
    faqHeading: "Domande frequenti su {style}",
    faqWhatIsQ: "Cos'è {style}?",
    faqWhenQ: "Quando devo usare {style}?",
    faqInTextQ: "Come si presenta una citazione nel testo in {style}?",
    faqInTextA:
      "{style} è {format}. Citare il nostro esempio di articolo di rivista nel testo si presenta così: {example}",
    faqReferenceQ:
      "Come si presenta una voce dell'elenco dei riferimenti in {style}?",
    faqReferenceA:
      "Un articolo di rivista formattato in {style} si presenta così: {example}",
    faqWhereQ: "Dove viene comunemente usato {style}?",
    faqWhereA: "{style} è usato {region}. {whenUsed}",
    faqRegionDefault: "a livello internazionale",
    faqGenerateQ: "Come posso generare una citazione in {style}?",
    faqGenerateA:
      "Usa il generatore gratuito in questa pagina: incolla un DOI, un URL (ad esempio un articolo di cronaca) o del BibTeX, oppure cerca un libro per titolo o autore. Seleziona {style} come stile di citazione e fai clic su Genera per ottenere un riferimento formattato da copiare nel tuo elaborato o nella tua bibliografia.",
    footerSupports:
      "supporta numerosi stili di citazione da tutto il mondo. Il nostro generatore utilizza gli stili Citation Style Language (CSL) del",
    footerRepoLink: "repository ufficiale CSL",
  },

  // The source × style pages at `/<locale>/how-to-cite/<slug>` — 1,290 combinations.
  //
  // Same division as above: the citation templates, the worked examples generated from
  // each style's own CSL definition, the in-text samples and the hand-checked
  // formatting rules stay in the page module and in
  // `src/data/how-to-cite-examples-generated.json`. They are the citations themselves,
  // not prose about them. Source names, style names and editions come from
  // `src/data/how-to-cite.ts` and arrive here as placeholder values.
  crossLinks: {
    generateHeading: "Genera questa citazione",
    generateBody:
      "Incolla un URL, DOI, ISBN o i dati della fonte e ottieni una citazione {style} pronta all'uso per {source}.",
    generateCta: "Apri il {tool} →",
    guideHeading: "Approfondisci",
    guideCta: "Leggi la guida completa: {guide}",
    howToCiteHeading: "Come citare {source} in ogni stile",
    howToCiteBody:
      "Regole di formattazione, modelli ed esempi svolti per gli stili più richiesti.",
    howToCiteMore: "Tutti i tipi di fonte e gli stili →",
    citeNowHeading: "Cita {source} in {style} adesso",
    citeNowHowTo: "Regole di formattazione ed esempi svolti",
    citeNowTool: "Generala con il {tool}",
    citeNowHub: "Generatore di citazioni {style}",
    hubSourcesHeading: "Come citare le fonti più comuni in {style}",
    hubGuidesHeading: "Guide {style}",
    hubCompareHeading: "Confronta {style} con altri stili",
    moreInCollection: "Altri strumenti: {collection}",
    seeAllInCollection: "Vedi tutti: {collection}",
  },

  howToCitePage: {
    notFoundTitle: "Non trovato",
    title: "Come citare {source} in {style} ({edition})",
    metaTitle: "Come citare {source} in {style} [{edition}, aggiornato 2026]",
    metaDescription:
      "Scopri come citare {description} nel formato {style} ({edition}). Esempi gratuiti, modelli e guide alle citazioni nel testo.",
    keywordCite: "citare {source} in {style}",
    keywordCitation: "citazione {style} per {source}",
    keywordHowTo: "come citare {source}",
    keywordExample: "esempio di citazione in {style}",
    keywordReference: "riferimento {style} per {source}",
    // What each style calls the list at the back of the paper, in the two casings the
    // prose needs. MLA says "Works Cited", Chicago and the legal styles "Bibliography".
    refListWorksCited: "Elenco delle opere citate",
    refListWorksCitedPage: "elenco delle opere citate",
    refListBibliography: "Bibliografia",
    refListBibliographyLower: "bibliografia",
    refListReferenceList: "Elenco dei riferimenti",
    refListReferenceListLower: "elenco dei riferimenti",
    backLink: "← Tutte le guide alla citazione",
    lead: "Scopri come citare {description} nel formato {style} ({edition}). Questa guida include il formato dell'elenco dei riferimenti, esempi di citazioni nel testo e le principali regole di formattazione.",
    referenceFormatHeading: "Formato {list} in {style}",
    referenceFormatIntro:
      "Ecco il formato standard {style} per citare {source} nella sezione {list}:",
    noTemplateNote:
      "Segui il modello standard di {style} per questo tipo di fonte.",
    workedHeading: "Esempio {style} per {source}",
    workedIntro:
      "Un riferimento completo in {style} per {source}, formattato secondo la definizione di stile CSL ufficiale di {style} — lo stesso motore che alimenta il generatore di questo sito:",
    workedNoteOnlyIntro:
      "{style} non inserisce {source} nella sezione {list}: la fonte viene citata solo in una nota. Ecco la nota, formattata secondo la definizione di stile CSL ufficiale di {style}:",
    workedOscolaNote:
      "L'output OSCOLA generato su questo sito segue lo stile CSL della 4ª edizione, perché nel repository Citation Style Language non è ancora stato pubblicato uno stile CSL della 5ª edizione. Correggilo a mano: sostituisci ogni ibid con il nome abbreviato e (n X), usa gli identificatori di tribunale della 5ª edizione, metti la forma abbreviata di una legge tra virgolette semplici e preferisci un link permanente o un DOI alla data di accesso",
    workedCheckedIntro:
      "Un riferimento completo in {style} per {source}, scritto a mano e verificato sulle indicazioni ufficiali di {style} per questo tipo di fonte:",
    workedCheckedNoteOnlyIntro:
      "{style} non inserisce {source} nella sezione {list}: la fonte viene citata solo in una nota. Ecco la nota, scritta a mano e verificata sulle indicazioni ufficiali di {style}:",
    workedInTextLabel: "Citazione nel testo corrispondente:",
    inTextHeading: "Citazione nel testo in {style}",
    inTextIntro:
      "Usa uno di questi formati quando citi questa fonte all'interno del tuo elaborato:",
    inTextParenthetical: "Parentetica",
    inTextNarrative: "Narrativa",
    rulesHeading: "Regole di formattazione principali in {style}",
    stepsHeading: "Guida passo passo",
    step1Label: "Raccogli le informazioni sulla fonte:",
    step1Body:
      "Per {source}, individua il nome dell'autore, il titolo, la data di pubblicazione e l'URL o il DOI.",
    step2Label: "Formatta il riferimento:",
    step2Body:
      "Disponi gli elementi seguendo il modello {style} ({edition}) mostrato sopra.",
    step3Label: "Crea la citazione nel testo:",
    step3Body:
      "Aggiungi una citazione tra parentesi o narrativa nel tuo elaborato nel punto in cui citi questa fonte.",
    step4Label: "Aggiungi alla sezione {list}:",
    step4Body:
      "Includi la citazione completa formattata alla fine del tuo elaborato.",
    step5Label: "Ricontrolla la formattazione:",
    step5Body:
      "Verifica che corsivo, punteggiatura, maiuscole e rientri sporgenti rispettino i requisiti di {style}.",
    ctaHeading: "Genera questa citazione automaticamente",
    ctaBody:
      "Salta la formattazione manuale. Incolla un DOI, un URL o un ISBN in CitationEasy e ottieni una citazione {style} perfettamente formattata in pochi secondi.",
    ctaButton: "Cita in {style} ora →",
    otherStylesHeading: "Cita {source} in altri stili",
    comboLink: "{source} in {style}",
    otherSourcesHeading: "Cita altre fonti in {style}",
    resourcesHeading: "Altre risorse",
    resourceGenerator: "Generatore di citazioni gratuito ({count} stili)",
    resourceTools: "Strumenti di scrittura gratuiti",
    resourceCompare: "Confronta stili di citazione",
    resourceGuides: "Tutte le guide alla citazione",
    resourceCheatSheets: "Schede riassuntive (APA, MLA, Chicago)",
    resourceGlossary:
      "Glossario delle citazioni (et al., ibid., DOI, rientro sporgente)",
    // HowTo structured data. Deliberately worded differently from the visible steps
    // above: the schema describes the task generically, the page walks the reader
    // through this particular source and style.
    schemaStep1Name: "Raccogli le informazioni sulla fonte",
    schemaStep1Text:
      "Raccogli tutte le informazioni necessarie per citare {source}, tra cui autore, titolo, data e URL o DOI.",
    schemaStep2Name: "Formatta in {style}",
    schemaStep2Text:
      "Segui le regole di formattazione {style} ({edition}) per disporre gli elementi della citazione nell'ordine corretto.",
    schemaStep3Name: "Crea la citazione nel testo",
    schemaStep3Text:
      "Aggiungi la corrispondente citazione nel testo nel tuo elaborato usando il formato {style}.",
    schemaStep4Name: "Aggiungi all'elenco dei riferimenti",
    schemaStep4Text:
      "Includi la citazione completa in {list}, alla fine del tuo elaborato.",
    faqHowQ: "Come si cita {source} in {style}?",
    faqHowA:
      "Per citare {source} in {style} ({edition}), includi autore, titolo, data e collocazione della fonte. Usa il nostro generatore di citazioni gratuito per la formattazione automatica.",
    faqFormatQ: "Qual è il formato {style} per citare {source}?",
    faqFormatA: "Il formato {style} per {source} è: {example}",
    faqFormatFallback:
      "Il formato {style} richiede elementi specifici, come autore, titolo, data e collocazione della fonte, disposti in un ordine particolare.",
    faqInTextQ:
      "Come si presenta la citazione nel testo per {source} in {style}?",
    faqInTextA:
      "In {style}, la citazione nel testo per l'esempio sopra è {example}",
  },

  // The 43 source types the how-to-cite pages are built from (`SOURCE_TYPES` in
  // `src/data/how-to-cite.ts`), in the five forms those pages print. Each form is a whole
  // phrase, never an article and a noun joined in code: an article agrees with its noun
  // in gender, number and case, elides in French and Italian, and has no counterpart
  // at all in Polish, Russian or Japanese.
  //
  // - `…Name` ("Book") is the type on its own, as a label: `howToCitePage.comboLink`.
  //   English also lower-cases it into the `keyword*` templates.
  // - `…Title` ("a Book") is the type as the object of "cite" in a title or heading:
  //   `title`, `metaTitle`, `workedHeading` and `otherStylesHeading`, plus
  //   `crossLinks.generateBody`, `howToCiteHeading` and `citeNowHeading`. Through
  //   `title` it is also the HowTo name, the OG card and every link to the page.
  // - `…Phrase` ("a book") is the same inside a sentence: `referenceFormatIntro`,
  //   `workedIntro`, `workedNoteOnlyIntro` and the five `faq*` questions and answers.
  // - `…Noun` ("book") is the bare noun inside a sentence, where English puts "your" in
  //   front of it: `step1Body` and `schemaStep1Text`. A translation also uses it in the
  //   `keyword*` templates, which read as search queries.
  // - `…Description` is what the type covers: `{description}` in `lead` and
  //   `metaDescription`.
  //
  // A language with case picks one case per form and words every template above around
  // it — German writes Title and Phrase in the accusative, Polish and Russian write Name
  // and Noun in the nominative. Changing a form's case means re-reading every template
  // that receives it. A key missing here falls back to the English data
  // (`howToCiteSourceForms`), so a new source type renders in English, not as a key.
  // Italian: Name is the bare label ("Libro"); Title, Phrase and Noun carry the article ("un libro", "un'intervista").
  howToCiteSources: {
    websiteName: "Sito web",
    websiteTitle: "un sito web",
    websitePhrase: "un sito web",
    websiteNoun: "un sito web",
    websiteDescription: "pagine web, articoli online e contenuti web",

    bookName: "Libro",
    bookTitle: "un libro",
    bookPhrase: "un libro",
    bookNoun: "un libro",
    bookDescription: "libri cartacei ed elettronici",

    journalArticleName: "Articolo di rivista",
    journalArticleTitle: "un articolo di rivista",
    journalArticlePhrase: "un articolo di rivista",
    journalArticleNoun: "un articolo di rivista",
    journalArticleDescription:
      "articoli di riviste accademiche e con revisione paritaria",

    youtubeVideoName: "Video di YouTube",
    youtubeVideoTitle: "un video di YouTube",
    youtubeVideoPhrase: "un video di YouTube",
    youtubeVideoNoun: "un video di YouTube",
    youtubeVideoDescription: "video di YouTube e altri video online",

    podcastName: "Podcast",
    podcastTitle: "un podcast",
    podcastPhrase: "un podcast",
    podcastNoun: "un podcast",
    podcastDescription: "episodi e serie di podcast",

    newspaperArticleName: "Articolo di giornale",
    newspaperArticleTitle: "un articolo di giornale",
    newspaperArticlePhrase: "un articolo di giornale",
    newspaperArticleNoun: "un articolo di giornale",
    newspaperArticleDescription: "articoli di giornali cartacei e online",

    magazineArticleName: "Articolo di magazine",
    magazineArticleTitle: "un articolo di magazine",
    magazineArticlePhrase: "un articolo di magazine",
    magazineArticleNoun: "un articolo di magazine",
    magazineArticleDescription: "articoli di magazine cartacei e online",

    blogPostName: "Articolo di blog",
    blogPostTitle: "un articolo di blog",
    blogPostPhrase: "un articolo di blog",
    blogPostNoun: "un articolo di blog",
    blogPostDescription: "post di blog e articoli d'opinione online",

    wikipediaName: "Wikipedia",
    wikipediaTitle: "Wikipedia",
    wikipediaPhrase: "Wikipedia",
    wikipediaNoun: "Wikipedia",
    wikipediaDescription: "voci di Wikipedia e di altri wiki",

    pdfName: "PDF",
    pdfTitle: "un PDF",
    pdfPhrase: "un PDF",
    pdfNoun: "un PDF",
    pdfDescription: "documenti e rapporti in formato PDF",

    interviewName: "Intervista",
    interviewTitle: "un'intervista",
    interviewPhrase: "un'intervista",
    interviewNoun: "un'intervista",
    interviewDescription: "interviste personali, pubblicate o trasmesse",

    socialMediaName: "Post sui social media",
    socialMediaTitle: "un post sui social media",
    socialMediaPhrase: "un post sui social media",
    socialMediaNoun: "un post sui social media",
    socialMediaDescription:
      "tweet, post di Instagram o Facebook e video di TikTok",

    governmentDocumentName: "Documento governativo",
    governmentDocumentTitle: "un documento governativo",
    governmentDocumentPhrase: "un documento governativo",
    governmentDocumentNoun: "un documento governativo",
    governmentDocumentDescription:
      "rapporti governativi, leggi e documenti ufficiali",

    dissertationName: "Tesi di laurea o di dottorato",
    dissertationTitle: "una tesi di laurea o di dottorato",
    dissertationPhrase: "una tesi di laurea o di dottorato",
    dissertationNoun: "una tesi di laurea o di dottorato",
    dissertationDescription: "tesi di dottorato e di laurea magistrale",

    conferencePaperName: "Relazione a convegno",
    conferencePaperTitle: "una relazione a convegno",
    conferencePaperPhrase: "una relazione a convegno",
    conferencePaperNoun: "una relazione a convegno",
    conferencePaperDescription: "atti di convegno e presentazioni",

    lectureNotesName: "Lezione o PowerPoint",
    lectureNotesTitle: "una lezione o un PowerPoint",
    lectureNotesPhrase: "una lezione o un PowerPoint",
    lectureNotesNoun: "una lezione o un PowerPoint",
    lectureNotesDescription: "lezioni in aula, slide e materiali didattici",

    webinarName: "Webinar",
    webinarTitle: "un webinar",
    webinarPhrase: "un webinar",
    webinarNoun: "un webinar",
    webinarDescription: "webinar e seminari online, in diretta o registrati",

    onlineCourseName: "Corso online",
    onlineCourseTitle: "un corso online",
    onlineCoursePhrase: "un corso online",
    onlineCourseNoun: "un corso online",
    onlineCourseDescription:
      "corsi online e MOOC (Coursera, edX, Udemy, FutureLearn)",

    presentationSlidesName: "Presentazione PowerPoint",
    presentationSlidesTitle: "una presentazione PowerPoint",
    presentationSlidesPhrase: "una presentazione PowerPoint",
    presentationSlidesNoun: "una presentazione PowerPoint",
    presentationSlidesDescription:
      "presentazioni PowerPoint e altre presentazioni con slide",

    imageName: "Immagine o fotografia",
    imageTitle: "un'immagine o una fotografia",
    imagePhrase: "un'immagine o una fotografia",
    imageNoun: "un'immagine o una fotografia",
    imageDescription: "fotografie, opere d'arte, grafici e immagini online",

    movieName: "Film",
    movieTitle: "un film",
    moviePhrase: "un film",
    movieNoun: "un film",
    movieDescription: "un film",

    documentaryName: "Documentario",
    documentaryTitle: "un documentario",
    documentaryPhrase: "un documentario",
    documentaryNoun: "un documentario",
    documentaryDescription: "film documentari e altri film non di finzione",

    tvShowName: "Programma TV",
    tvShowTitle: "un programma TV",
    tvShowPhrase: "un programma TV",
    tvShowNoun: "un programma TV",
    tvShowDescription: "un programma o una serie TV",

    songName: "Canzone o brano musicale",
    songTitle: "una canzone o un brano musicale",
    songPhrase: "una canzone o un brano musicale",
    songNoun: "una canzone o un brano musicale",
    songDescription: "una canzone o una registrazione musicale",

    poemName: "Poesia",
    poemTitle: "una poesia",
    poemPhrase: "una poesia",
    poemNoun: "una poesia",
    poemDescription: "poesie e versi tratti da antologie o raccolte",

    courtCaseName: "Causa giudiziaria",
    courtCaseTitle: "una causa giudiziaria",
    courtCasePhrase: "una causa giudiziaria",
    courtCaseNoun: "una causa giudiziaria",
    courtCaseDescription: "una causa giudiziaria o una sentenza",

    patentName: "Brevetto",
    patentTitle: "un brevetto",
    patentPhrase: "un brevetto",
    patentNoun: "un brevetto",
    patentDescription:
      "un brevetto o un altro deposito di proprietà intellettuale",

    encyclopediaName: "Voce di enciclopedia",
    encyclopediaTitle: "una voce di enciclopedia",
    encyclopediaPhrase: "una voce di enciclopedia",
    encyclopediaNoun: "una voce di enciclopedia",
    encyclopediaDescription: "un articolo o una voce di enciclopedia",

    dictionaryName: "Voce di dizionario",
    dictionaryTitle: "una voce di dizionario",
    dictionaryPhrase: "una voce di dizionario",
    dictionaryNoun: "una voce di dizionario",
    dictionaryDescription: "una definizione o una voce di dizionario",

    datasetName: "Dataset",
    datasetTitle: "un dataset",
    datasetPhrase: "un dataset",
    datasetNoun: "un dataset",
    datasetDescription: "un dataset o una banca dati",

    softwareName: "Software o app",
    softwareTitle: "un software o un'app",
    softwarePhrase: "un software o un'app",
    softwareNoun: "un software o un'app",
    softwareDescription: "un software, un'app o un programma informatico",

    videoGameName: "Videogioco",
    videoGameTitle: "un videogioco",
    videoGamePhrase: "un videogioco",
    videoGameNoun: "un videogioco",
    videoGameDescription: "videogiochi e software di gioco interattivi",

    pressReleaseName: "Comunicato stampa",
    pressReleaseTitle: "un comunicato stampa",
    pressReleasePhrase: "un comunicato stampa",
    pressReleaseNoun: "un comunicato stampa",
    pressReleaseDescription: "un comunicato stampa o un comunicato ufficiale",

    reportName: "Rapporto",
    reportTitle: "un rapporto",
    reportPhrase: "un rapporto",
    reportNoun: "un rapporto",
    reportDescription: "un rapporto o un libro bianco",

    preprintName: "Preprint",
    preprintTitle: "un preprint",
    preprintPhrase: "un preprint",
    preprintNoun: "un preprint",
    preprintDescription:
      "articoli depositati su arXiv, bioRxiv e altri server di preprint",

    bookChapterName: "Capitolo di libro",
    bookChapterTitle: "un capitolo di libro",
    bookChapterPhrase: "un capitolo di libro",
    bookChapterNoun: "un capitolo di libro",
    bookChapterDescription: "capitoli e saggi in volumi collettanei",

    chatgptName: "ChatGPT",
    chatgptTitle: "ChatGPT",
    chatgptPhrase: "ChatGPT",
    chatgptNoun: "ChatGPT",
    chatgptDescription: "ChatGPT e altri strumenti di IA generativa",

    bibleName: "Bibbia",
    bibleTitle: "la Bibbia",
    biblePhrase: "la Bibbia",
    bibleNoun: "la Bibbia",
    bibleDescription: "la Bibbia, le Scritture e altri testi sacri",

    tedTalkName: "Conferenza TED",
    tedTalkTitle: "una conferenza TED",
    tedTalkPhrase: "una conferenza TED",
    tedTalkNoun: "una conferenza TED",
    tedTalkDescription: "conferenze TED e altri interventi registrati",

    emailName: "E-mail",
    emailTitle: "un'e-mail",
    emailPhrase: "un'e-mail",
    emailNoun: "un'e-mail",
    emailDescription: "e-mail, lettere e comunicazioni personali",

    redditName: "Post di Reddit o di un forum",
    redditTitle: "un post di Reddit o di un forum",
    redditPhrase: "un post di Reddit",
    redditNoun: "un post di Reddit",
    redditDescription:
      "thread di Reddit, AMA e post di forum o gruppi di discussione online",

    tweetName: "Post su X (Twitter)",
    tweetTitle: "un post su X (Twitter)",
    tweetPhrase: "un post su X",
    tweetNoun: "un post su X",
    tweetDescription: "tweet e post su X (ex Twitter)",

    mapName: "Mappa",
    mapTitle: "una mappa",
    mapPhrase: "una mappa",
    mapNoun: "una mappa",
    mapDescription:
      "mappe cartacee, atlanti e mappe interattive come Google Maps",

    claudeName: "Claude",
    claudeTitle: "Claude",
    claudePhrase: "Claude",
    claudeNoun: "Claude",
    claudeDescription:
      "conversazioni con Claude e altri contenuti generati dall'IA di Anthropic",

    geminiName: "Gemini",
    geminiTitle: "Gemini",
    geminiPhrase: "Gemini",
    geminiNoun: "Gemini",
    geminiDescription:
      "conversazioni con Google Gemini e contenuti generati dall'IA",

    copilotName: "Microsoft Copilot",
    copilotTitle: "Microsoft Copilot",
    copilotPhrase: "Microsoft Copilot",
    copilotNoun: "Microsoft Copilot",
    copilotDescription: "conversazioni e risposte IA di Microsoft Copilot",

    perplexityName: "Perplexity",
    perplexityTitle: "Perplexity",
    perplexityPhrase: "Perplexity",
    perplexityNoun: "Perplexity",
    perplexityDescription:
      "risposte e thread di Perplexity e le fonti che citano",

    aiImageName: "Immagine generata dall'IA",
    aiImageTitle: "un'immagine generata dall'IA",
    aiImagePhrase: "un'immagine generata dall'IA",
    aiImageNoun: "un'immagine generata dall'IA",
    aiImageDescription:
      "immagini create con DALL·E, Midjourney, Gemini e altri strumenti di IA",

    tiktokName: "Video di TikTok",
    tiktokTitle: "un video di TikTok",
    tiktokPhrase: "un video di TikTok",
    tiktokNoun: "un video di TikTok",
    tiktokDescription: "video di TikTok e post dei creator",

    instagramName: "Post di Instagram",
    instagramTitle: "un post di Instagram",
    instagramPhrase: "un post di Instagram",
    instagramNoun: "un post di Instagram",
    instagramDescription: "post, reel e storie di Instagram",

    statuteName: "Legge o atto normativo",
    statuteTitle: "una legge o un atto normativo",
    statutePhrase: "una legge o un atto normativo",
    statuteNoun: "una legge o un atto normativo",
    statuteDescription:
      "leggi approvate dal Parlamento, atti normativi e codici",

    isoStandardName: "Norma ISO",
    isoStandardTitle: "una norma ISO",
    isoStandardPhrase: "una norma ISO",
    isoStandardNoun: "una norma ISO",
    isoStandardDescription: "norme ISO, IEC e altre norme tecniche pubblicate",

    quranName: "Corano",
    quranTitle: "il Corano",
    quranPhrase: "il Corano",
    quranNoun: "il Corano",
    quranDescription: "il Corano, in arabo o in traduzione",

    ebookNoPageNumbersName: "E-book senza numeri di pagina",
    ebookNoPageNumbersTitle: "un e-book senza numeri di pagina",
    ebookNoPageNumbersPhrase: "un e-book senza numeri di pagina",
    ebookNoPageNumbersNoun: "un e-book senza numeri di pagina",
    ebookNoPageNumbersDescription:
      "e-book Kindle, EPUB e online senza numeri di pagina fissi",

    speechName: "Discorso",
    speechTitle: "un discorso",
    speechPhrase: "un discorso",
    speechNoun: "un discorso",
    speechDescription: "discorsi, allocuzioni e sermoni, dal vivo o registrati",

    letterName: "Lettera",
    letterTitle: "una lettera",
    letterPhrase: "una lettera",
    letterNoun: "una lettera",
    letterDescription:
      "lettere pubblicate, lettere d'archivio e lettere aperte",

    secondarySourceName: "Fonte secondaria",
    secondarySourceTitle: "una fonte secondaria",
    secondarySourcePhrase: "una fonte secondaria",
    secondarySourceNoun: "una fonte secondaria",
    secondarySourceDescription:
      "una fonte trovata citata all'interno di un'altra opera",
  },

  // ---------------------------------------------------------------------------
  // NOT YET TRANSLATED. These five sections were copied verbatim from `en.ts` so
  // this file still satisfies `Dictionary`; the page types they cover
  // (`taxonomy`, `compare`, `convert`) are deliberately absent from
  // TRANSLATED_PAGE_TYPES, so nothing publishes them in this language yet. The
  // translation pass replaces the strings and only then adds the types.
  // ---------------------------------------------------------------------------
  // The three browse-by-facet pages under `/<locale>/citation-styles/…` — 32 academic
  // fields, 68 countries and 39 subject categories, 139 pages in one section because
  // they share a shell, a breadcrumb trail and a styles browser.
  //
  // The style NAMES, slugs and the CSL catalog itself stay in `src/data/**`: those are
  // subject matter. What the facets themselves are called is not — "Political Science",
  // "United Kingdom" and "Film & Media" are ordinary nouns that every reader expects in
  // their own language, and there are only 139 of them in total (see the `academicFields`,
  // `regions` and `styleCategories` sections below).
  taxonomyPage: {
    // ---- /citation-styles/field/<slug> ----
    fieldNotFoundTitle: "Non trovato",
    fieldMetaTitle:
      "Stili di citazione in {field} - I migliori stili di riferimento [2026]",
    fieldMetaDescription:
      "Scopri gli stili di citazione più utilizzati in {field}. Trova il formato di riferimento adatto alla tua tesi, tesina o articolo di rivista in {fieldLower}.",
    fieldOgTitle: "Stili di citazione in {field}",
    fieldOgSubtitle: "Generalmente richiesti: {styles}.",
    fieldOgKind: "Per disciplina accademica",
    fieldKeywordStyle: "stile di citazione in {field}",
    fieldKeywordReferencing: "riferimenti bibliografici in {field}",
    fieldKeywordBest: "miglior stile di citazione in {fieldLower}",
    fieldKeywordBibliography: "formato bibliografia in {fieldLower}",
    fieldKeywordDiscipline: "stili di citazione per disciplina",
    fieldBackLink: "← Tutti gli stili di citazione",
    fieldHeading: "Stili di citazione in {field}",
    fieldRecommendedHeading: "Stili di citazione consigliati",
    fieldRecommendedIntro:
      "I seguenti stili di citazione sono comunemente usati nella ricerca, nelle riviste e nei corsi universitari di {field}. Seleziona uno stile per saperne di più e iniziare a generare citazioni.",
    fieldStyleFallback:
      "Uno stile di citazione usato in {field} e nelle discipline correlate.",
    fieldRelatedHeading: "Ambiti correlati",
    fieldRelatedIntro:
      "Scopri gli stili di citazione usati nelle discipline accademiche correlate:",
    fieldCtaHeading: "Scopri tutti gli stili di citazione",
    fieldCtaBody:
      "CitationEasy supporta {count} stili di citazione in tutte le discipline accademiche. Sfoglia la raccolta completa per trovare esattamente il formato di cui hai bisogno.",
    fieldCtaButton: "Sfoglia tutti gli stili →",
    fieldFaqWhichQ: "Quale stile di citazione si usa in {field}?",
    fieldFaqWhichA:
      "Lo stile di citazione usato in {field} dipende dalla rivista specifica, dall'istituto o dalle richieste del docente. Gli stili più comuni includono {styles}. Verifica sempre le indicazioni del tuo elaborato o i requisiti della rivista di destinazione per conoscere il formato richiesto.",
    fieldFaqSameQ: "Tutte le riviste di {field} usano lo stesso stile?",
    fieldFaqSameA:
      "No, le riviste di {field} non usano tutte lo stesso stile di citazione. Alcuni stili sono più comuni in questo ambito, ma ogni rivista può avere le proprie regole di formattazione. Verifica sempre le linee guida per l'invio della rivista prima di preparare il tuo manoscritto.",
    fieldFaqChooseQ:
      "Come scelgo uno stile di citazione per la mia tesina di {field}?",
    fieldFaqChooseA:
      "Per scegliere uno stile di citazione per la tua tesina di {field}, verifica prima se il tuo docente o la rivista di destinazione richiede uno stile specifico. In caso contrario, usa uno degli stili comunemente consigliati per {field} elencati sopra. CitationEasy supporta oltre {count} stili e può generare citazioni in uno qualsiasi di essi, gratuitamente.",

    // ---- /citation-styles/country/<slug> ----
    countryNotFoundTitle: "Paese non trovato",
    countryMetaTitle: "Stili di citazione per {country} - APA, MLA e altri",
    countryMetaDescription:
      "Sfoglia gli stili di citazione usati per {country}. Generatore di citazioni gratuito per {country}.",
    countryOgTitle: "Stili di citazione usati per {country}",
    countryOgKind: "Per paese",
    countryHeading: "{country} – Stili di citazione",
    countryIntroOne:
      "{count} stile di citazione comunemente usato per {country} o associato a questo paese. Selezionane uno per aprire un generatore dedicato.",
    countryIntroMany:
      "{count} stili di citazione comunemente usati per {country} o associati a questo paese. Selezionane uno per aprire un generatore dedicato.",
    countryBrowserTitle: "{country} – Stili di citazione",

    // ---- /citation-styles/type/<slug> ----
    typeNotFoundTitle: "Tipo non trovato",
    typeMetaTitle: "Stili di citazione in {type} - Generatore gratuito",
    typeMetaDescription:
      "Stili di citazione in {typeLower}. APA, MLA, Vancouver e altri. Generatore di citazioni gratuito.",
    typeOgTitle: "Stili di citazione in {type}",
    typeOgKind: "Per materia",
    typeHeading: "Stili di citazione in {type}",
    typeIntroOne:
      "{count} stile di citazione in {typeLower}. Selezionane uno per aprire un generatore dedicato con FAQ.",
    typeIntroMany:
      "{count} stili di citazione in {typeLower}. Selezionane uno per aprire un generatore dedicato con FAQ.",
    typeBrowserTitle: "Stili di citazione in {type}",

    // ---- Shared chrome ----
    breadcrumbStyles: "Stili di citazione",
    // Title case, because the BreadcrumbList markup says "Citation Styles" while the
    // visible trail says "Citation styles". Two keys rather than one so translating
    // either does not silently change the other.
    breadcrumbStylesSchema: "Stili di citazione",
    breadcrumbByCountry: "Per paese",
    breadcrumbByDiscipline: "Per disciplina",
    allStylesLink: "← Tutti gli stili di citazione",
    internationalLabel: "Internazionale",
  },

  // The 32 academic fields (`ACADEMIC_FIELDS` in src/data/style-pages.ts), keyed by
  // slug. Their one-sentence `description` is next door in `academicFieldDescriptions`.
  academicFields: {
    psychology: "Psicologia",
    education: "Scienze dell'educazione",
    sociology: "Sociologia",
    anthropology: "Antropologia",
    politicalScience: "Scienze politiche",
    economics: "Economia",
    business: "Economia e gestione aziendale",
    literature: "Letteratura e lingue",
    history: "Storia",
    philosophy: "Filosofia",
    law: "Diritto e scienze giuridiche",
    medicine: "Medicina e scienze cliniche",
    nursing: "Infermieristica e ostetricia",
    publicHealth: "Sanità pubblica ed epidemiologia",
    pharmacy: "Farmacia",
    biology: "Biologia e scienze della vita",
    chemistry: "Chimica",
    physics: "Fisica",
    engineering: "Ingegneria",
    computerScience: "Informatica",
    mathematics: "Matematica e statistica",
    environmentalScience: "Scienze ambientali",
    earthScience: "Scienze della Terra e geologia",
    artHistory: "Storia dell'arte e arti visive",
    music: "Musica e musicologia",
    theology: "Teologia e studi religiosi",
    socialWork: "Servizio sociale",
    linguistics: "Linguistica",
    architecture: "Architettura e urbanistica",
    filmMedia: "Studi cinematografici e mediali",
    journalism: "Giornalismo e comunicazione",
    criminology: "Criminologia e giustizia penale",
  },

  // The same 32 fields' one-sentence ledes — the opening paragraph of every
  // `/citation-styles/field/<slug>` page.
  //
  // Unlike everything above, these are hand-written per field rather than mail-merged
  // from a template, which is why the extraction pass left them in
  // `src/data/style-pages.ts`: translating them is a content job, not a string swap.
  // But "hand-written" describes how they were produced, not where they belong. With
  // the chrome translated and the lede still English, the page is the hybrid CLAUDE.md
  // calls worse than an honest redirect — translated furniture around an English core.
  // `academicFieldDescription()` (src/lib/i18n/data-labels.ts) falls back to the data
  // file's own value, so a field added upstream is untranslated rather than blank.
  academicFieldDescriptions: {
    psychology:
      "Stili di citazione comunemente usati in psicologia, scienze comportamentali e ricerca cognitiva.",
    education:
      "Stili di citazione per la ricerca in ambito educativo, la pedagogia e gli studi curricolari.",
    sociology:
      "Stili di citazione usati nella ricerca sociologica e nella teoria sociale.",
    anthropology:
      "Stili di citazione per la ricerca antropologica ed etnografica.",
    politicalScience:
      "Stili di citazione per le scienze politiche, la pubblica amministrazione e la ricerca sulle politiche pubbliche.",
    economics:
      "Stili di citazione usati nella ricerca economica ed econometrica.",
    business:
      "Stili di citazione per la ricerca in economia aziendale, gestione, marketing e finanza.",
    literature:
      "Stili di citazione per gli studi letterari, la linguistica e la ricerca sul linguaggio.",
    history:
      "Stili di citazione per la ricerca storica, la storiografia e gli studi archivistici.",
    philosophy:
      "Stili di citazione per la scrittura filosofica e la ricerca in etica.",
    law: "Stili di citazione giuridica per atti giudiziari, riviste di diritto e produzione accademica in ambito legale.",
    medicine:
      "Stili di citazione per la ricerca medica, gli studi clinici e la sanità.",
    nursing:
      "Stili di citazione per la ricerca infermieristica e la pratica sanitaria.",
    publicHealth:
      "Stili di citazione per la ricerca in sanità pubblica e gli studi epidemiologici.",
    pharmacy: "Stili di citazione per la ricerca farmaceutica e farmacologica.",
    biology:
      "Stili di citazione per la ricerca biologica, l'ecologia e la genetica.",
    chemistry:
      "Stili di citazione per chimica, biochimica e ricerca in scienza dei materiali.",
    physics:
      "Stili di citazione per fisica, astrofisica e ricerca in fisica applicata.",
    engineering:
      "Stili di citazione per l'ingegneria e la ricerca in tecnologia applicata.",
    computerScience:
      "Stili di citazione per informatica, IA e ricerca in ingegneria del software.",
    mathematics:
      "Stili di citazione per matematica, matematica applicata e ricerca statistica.",
    environmentalScience:
      "Stili di citazione per la ricerca ambientale e gli studi sulla sostenibilità.",
    earthScience:
      "Stili di citazione per geologia, geofisica e ricerca sulle scienze della Terra.",
    artHistory:
      "Stili di citazione per la storia dell'arte, la cultura visiva e gli studi museali.",
    music:
      "Stili di citazione per la ricerca musicale, la teoria e gli studi sulla performance.",
    theology:
      "Stili di citazione per la produzione accademica teologica e religiosa.",
    socialWork:
      "Stili di citazione per la ricerca e la pratica nel servizio sociale.",
    linguistics:
      "Stili di citazione per la linguistica, la fonetica e la scienza del linguaggio.",
    architecture:
      "Stili di citazione per l'architettura, la progettazione urbana e la ricerca in pianificazione.",
    filmMedia:
      "Stili di citazione per gli studi cinematografici, televisivi, mediali e della comunicazione.",
    journalism:
      "Stili di citazione per il giornalismo, gli studi sui media e la ricerca in comunicazione.",
    criminology:
      "Stili di citazione per la criminologia e la ricerca in giustizia penale.",
  },

  // The 68 country/region facets (`REGIONS` in src/data/citation-styles.ts), keyed by
  // slug. `getRegionCountryCode` still keys off the English data value, so translating these
  // moves the label without moving the flag.
  regions: {
    unitedStates: "Stati Uniti",
    unitedKingdom: "Regno Unito",
    australia: "Australia",
    canada: "Canada",
    newZealand: "Nuova Zelanda",
    ireland: "Irlanda",
    southAfrica: "Sudafrica",
    germany: "Germania",
    france: "Francia",
    spain: "Spagna",
    italy: "Italia",
    netherlands: "Paesi Bassi",
    belgium: "Belgio",
    switzerland: "Svizzera",
    austria: "Austria",
    portugal: "Portogallo",
    luxembourg: "Lussemburgo",
    sweden: "Svezia",
    norway: "Norvegia",
    denmark: "Danimarca",
    finland: "Finlandia",
    iceland: "Islanda",
    poland: "Polonia",
    czechRepublic: "Repubblica Ceca",
    hungary: "Ungheria",
    romania: "Romania",
    russia: "Russia",
    ukraine: "Ucraina",
    greece: "Grecia",
    turkey: "Turchia",
    croatia: "Croazia",
    slovenia: "Slovenia",
    slovakia: "Slovacchia",
    serbia: "Serbia",
    bulgaria: "Bulgaria",
    lithuania: "Lituania",
    latvia: "Lettonia",
    estonia: "Estonia",
    japan: "Giappone",
    china: "Cina",
    southKorea: "Corea del Sud",
    india: "India",
    singapore: "Singapore",
    hongKong: "Hong Kong",
    taiwan: "Taiwan",
    malaysia: "Malesia",
    indonesia: "Indonesia",
    thailand: "Thailandia",
    vietnam: "Vietnam",
    philippines: "Filippine",
    pakistan: "Pakistan",
    bangladesh: "Bangladesh",
    sriLanka: "Sri Lanka",
    israel: "Israele",
    saudiArabia: "Arabia Saudita",
    unitedArabEmirates: "Emirati Arabi Uniti",
    brazil: "Brasile",
    mexico: "Messico",
    argentina: "Argentina",
    chile: "Cile",
    colombia: "Colombia",
    peru: "Perù",
    venezuela: "Venezuela",
    egypt: "Egitto",
    nigeria: "Nigeria",
    kenya: "Kenya",
    morocco: "Marocco",
    international: "Internazionale",
  },

  // The 39 subject categories (`CATEGORIES` in src/data/citation-styles.ts), keyed by
  // slug. The emoji beside each one stays in the data — it is language-neutral.
  styleCategories: {
    medicine: "Medicina",
    nursing: "Infermieristica",
    publicHealth: "Sanità pubblica",
    pharmacy: "Farmacia",
    dentistry: "Odontoiatria",
    veterinary: "Scienze veterinarie",
    psychology: "Psicologia",
    sociology: "Sociologia",
    anthropology: "Antropologia",
    politicalScience: "Scienze politiche",
    economics: "Economia",
    education: "Scienze dell'educazione",
    socialWork: "Servizio sociale",
    biology: "Biologia",
    chemistry: "Chimica",
    physics: "Fisica",
    environmentalScience: "Scienze ambientali",
    earthScience: "Scienze della Terra e geologia",
    agriculture: "Agricoltura",
    ecology: "Ecologia",
    engineering: "Ingegneria",
    computerScience: "Informatica",
    mathematics: "Matematica",
    statistics: "Statistica",
    history: "Storia",
    literature: "Letteratura",
    philosophy: "Filosofia",
    linguistics: "Linguistica",
    artHistory: "Storia dell'arte",
    music: "Musica",
    filmMedia: "Cinema e media",
    archaeology: "Archeologia",
    law: "Diritto",
    business: "Economia aziendale",
    architecture: "Architettura",
    religion: "Religione e teologia",
    geography: "Geografia",
    general: "Generale / Multidisciplinare",
    other: "Altro",
  },

  // The closed vocabularies `STYLE_LANDING_PAGES` (src/data/style-pages.ts) draws on for
  // the 30 major styles: what each style calls its end-of-paper list, the shape of its
  // in-text citation, its edition, and the disciplines that require it. The comparison
  // pages print all four in a table and the field pages print two of them under every
  // style card, so leaving them in the data means a translated page with an English
  // table in the middle of it.
  //
  // Style NAMES (`name`, `fullName`) are NOT here: a style's official name is what it
  // is called everywhere. The 30 hand-written taglines are, in `styleTaglines` below.
  styleFacts: {
    // `referenceListName` — 6 distinct values across the 30 styles.
    listReferenceList: "Elenco dei riferimenti",
    listWorksCited: "Elenco delle opere citate",
    listReferences: "Riferimenti",
    listBibliography: "Bibliografia",
    listTableOfAuthorities: "Tabella delle autorità citate",
    listReferencesCited: "Riferimenti citati",
    listLowerReferenceList: "elenco dei riferimenti",
    listLowerWorksCited: "elenco delle opere citate",
    listLowerReferences: "riferimenti",
    listLowerBibliography: "bibliografia",
    listLowerTableOfAuthorities: "tabella delle autorità citate",
    listLowerReferencesCited: "riferimenti citati",

    // `inTextFormat` — 14 distinct values. "Author", "Year" and "Page" are slot names,
    // not part of the citation, so they read as English words on a Spanish page.
    inTextAuthorCommaYear: "(Autore, Anno)",
    inTextAuthorPage: "(Autore Pagina)",
    inTextAuthorYear: "(Autore Anno)",
    inTextSuperscriptOrBracketed: "Numeri in apice o tra parentesi quadre (1)",
    inTextBracketedNumbers: "Numeri tra parentesi quadre [1]",
    inTextSuperscriptNumbers: "Numeri in apice¹",
    inTextSuperscriptOrAuthorYear: "Numeri in apice o (Autore Anno)",
    inTextKeyBased: "Riferimenti basati su chiave",
    inTextSuperscriptOrAuthorCommaYear: "Numeri in apice o (Autore, Anno)",
    inTextFootnotesCaseLegislation:
      "Note a piè di pagina con citazioni di giurisprudenza e normativa",
    inTextFootnotesSignals: "Note a piè di pagina con abbreviazioni di rinvio",
    inTextFootnotesPinpoint: "Note a piè di pagina con riferimenti puntuali",
    inTextParenNumbers: "Numeri tra parentesi (1)",
    inTextItalicNumbers: "Numeri in corsivo (1)",

    // `edition` — 12 distinct values. The ordinal and the word "edition" translate; the
    // handbook name inside "Cite Them Right 11th edition" does not.
    edition4th: "4ª edizione",
    edition5th: "5ª edizione",
    edition7th: "7ª edizione",
    edition8th: "8ª edizione",
    edition9th: "9ª edizione",
    edition11th: "11ª edizione",
    edition18th: "18ª edizione",
    edition22nd: "22ª edizione",
    editionCiteThemRight11th: "Cite Them Right, 11ª edizione",
    editionIcmjeStandard: "Standard ICMJE",
    editionStandard: "standard",
    editionCurrent: "attuale",

    // `fields` — the 67 discipline names the 30 styles are tagged with.
    fieldAcoustics: "Acustica",
    fieldAnthropology: "Antropologia",
    fieldAppliedPhysics: "Fisica applicata",
    fieldArchaeology: "Archeologia",
    fieldArts: "Arti",
    fieldAstrophysics: "Astrofisica",
    fieldAustralianLaw: "Diritto australiano",
    fieldBiochemistry: "Biochimica",
    fieldBiology: "Biologia",
    fieldBiomedicalSciences: "Scienze biomediche",
    fieldBiotechnology: "Biotecnologia",
    fieldBusiness: "Economia aziendale",
    fieldCellBiology: "Biologia cellulare",
    fieldChemicalEngineering: "Ingegneria chimica",
    fieldChemistry: "Chimica",
    fieldClinicalResearch: "Ricerca clinica",
    fieldComputerScience: "Informatica",
    fieldCondensedMatter: "Materia condensata",
    fieldCourtDocuments: "Documenti giudiziari",
    fieldCriminology: "Criminologia",
    fieldCulturalStudies: "Studi culturali",
    fieldDemography: "Demografia",
    fieldEcology: "Ecologia",
    fieldEducation: "Pedagogia",
    fieldElectronics: "Elettronica",
    fieldEngineering: "Ingegneria",
    fieldEnvironmentalScience: "Scienze ambientali",
    fieldEpidemiology: "Epidemiologia",
    fieldEthnography: "Etnografia",
    fieldGenetics: "Genetica",
    fieldGeology: "Geologia",
    fieldGlobalHealth: "Salute globale",
    fieldHealthSciences: "Scienze della salute",
    fieldHistory: "Storia",
    fieldHumanities: "Scienze umane",
    fieldInformationSystems: "Sistemi informativi",
    fieldInformationTechnology: "Tecnologie dell'informazione",
    fieldInternationalRelations: "Relazioni internazionali",
    fieldJurisprudence: "Giurisprudenza",
    fieldLanguages: "Lingue",
    fieldLaw: "Diritto",
    fieldLawReview: "Rivista giuridica",
    fieldLegalStudies: "Scienze giuridiche",
    fieldLifeSciences: "Scienze della vita",
    fieldLiterature: "Letteratura",
    fieldMaterialsScience: "Scienza dei materiali",
    fieldMathematics: "Matematica",
    fieldMedicine: "Medicina",
    fieldMolecularBiology: "Biologia molecolare",
    fieldMultidisciplinary: "Multidisciplinare",
    fieldMultidisciplinaryScience: "Scienze multidisciplinari",
    fieldNaturalSciences: "Scienze naturali",
    fieldNursing: "Infermieristica",
    fieldPharmacy: "Farmacia",
    fieldPhysics: "Fisica",
    fieldPoliticalScience: "Scienze politiche",
    fieldPsychology: "Psicologia",
    fieldPublicHealth: "Sanità pubblica",
    fieldPublicPolicy: "Politiche pubbliche",
    fieldPublishing: "Editoria",
    fieldScience: "Scienza",
    fieldSciences: "Scienze",
    fieldSocialSciences: "Scienze sociali",
    fieldSocialWork: "Servizio sociale",
    fieldSociology: "Sociologia",
    fieldSoftwareEngineering: "Ingegneria del software",
    fieldTheology: "Teologia",
  },

  // The 30 major styles' hero taglines (`STYLE_LANDING_PAGES[].tagline`), keyed by
  // slug.
  //
  // Per-page hand-written copy, not a template — which is why they outlived the
  // extraction pass in `src/data/style-pages.ts`. Three page types print them: the
  // style landing page's hero, the card for each recommended style on a field
  // taxonomy page, and the lower-cased clause inside a comparison page's opening
  // sentence ("APA (…) is the most widely used citation style in the social
  // sciences"). Leaving them in the data means an English sentence in the first
  // paragraph of an otherwise translated page. `styleTagline()`
  // (src/lib/i18n/data-labels.ts) falls back to the data file's own value.
  styleTaglines: {
    apa: "Lo stile di citazione più utilizzato nelle scienze sociali",
    mla: "Lo stile di citazione standard per le discipline umanistiche",
    chicago:
      "La guida di stile più autorevole degli Stati Uniti per autori, redattori ed editori",
    harvard:
      "Lo stile di riferimento più diffuso nelle università del Regno Unito e dell'Australia",
    vancouver:
      "Lo stile di citazione standard per le riviste mediche e biomediche",
    ieee: "Lo stile di citazione di riferimento per l'ingegneria e l'informatica",
    ama: "Lo standard di citazione delle riviste mediche statunitensi",
    turabian: "La versione pensata per gli studenti dello stile Chicago",
    cse: "Lo stile di citazione preferito nelle scienze naturali e fisiche",
    bibtex:
      "Il formato di bibliografia per chi usa LaTeX e per l'editoria accademica",
    nature:
      "Lo stile di citazione usato dalla principale rivista multidisciplinare al mondo",
    acs: "Lo standard di citazione per la chimica e le scienze chimiche",
    oscola:
      "Lo stile di citazione giuridica standard nelle facoltà di giurisprudenza e nelle riviste del Regno Unito",
    bluebook:
      "Il sistema di citazione giuridica di riferimento per i tribunali e le riviste di diritto statunitensi",
    aglc: "Lo stile di citazione giuridica standard nelle facoltà di giurisprudenza e nei tribunali australiani",
    asa: "Lo stile di citazione standard per la sociologia e la ricerca sociale",
    apsa: "Lo stile di citazione per la ricerca e le riviste di scienze politiche",
    aaa: "Lo stile di citazione standard per la ricerca antropologica",
    nlm: "Lo standard di citazione usato da PubMed e dalla Biblioteca nazionale di medicina degli Stati Uniti",
    acm: "Lo stile di citazione per le conferenze ACM e la ricerca in informatica",
    aps: "Lo stile di citazione di Physical Review e delle riviste APS",
    aip: "Lo stile di citazione di AIP e delle riviste di fisica",
    cell: "Lo stile di citazione di Cell Press e delle principali riviste di biologia",
    science:
      "Lo stile di citazione della rivista Science e delle pubblicazioni AAAS",
    plos: "Lo stile di citazione delle riviste ad accesso aperto di PLOS",
    bmj: "Lo stile di citazione di BMJ e delle principali riviste mediche britanniche",
    lancet:
      "Lo stile di citazione della famiglia di riviste mediche The Lancet",
    jama: "Lo stile di citazione di JAMA e delle riviste JAMA Network",
    elsevierHarvard:
      "La variante dello stile Harvard usata nelle riviste Elsevier",
    springer: "Lo stile di citazione delle riviste e dei libri Springer Nature",
  },

  // The 105 style-versus-style pages at `/<locale>/compare/<slug>`.
  //
  // The two style names arrive as placeholder values and are never translated. The
  // facts in the table come from `styleFacts` above and the taglines from
  // `styleTaglines`; only `fullName` stays in `src/data/style-pages.ts`, because an
  // official name is the same in every language.
  comparePage: {
    notFoundTitle: "Non trovato",
    metaTitle: "{a} vs {b} - Le differenze principali spiegate [2026]",
    metaDescription:
      "Confronta gli stili di citazione {a} e {b} fianco a fianco. Scopri le principali differenze di formattazione, citazioni nel testo ed elenchi dei riferimenti per scegliere lo stile giusto per il tuo elaborato.",
    ogTitle: "{a} vs {b}",
    ogSubtitle:
      "Formato nel testo, voci bibliografiche, uso delle maiuscole e discipline che richiedono ciascuno stile.",
    ogKind: "Confronto tra stili",
    keywordVs: "{a} vs {b}",
    keywordVersus: "{a} contro {b}",
    keywordDifference: "differenza tra {a} e {b}",
    keywordOr: "{a} o {b}",
    keywordComparison: "confronto tra stili di citazione",
    breadcrumbCompare: "Confronta stili",
    breadcrumbCurrent: "{a} vs {b}",
    backLink: "← Torna allo strumento di confronto",
    heading: "{a} vs {b}: differenze principali",
    intro:
      "{a} e {b} sono tra gli stili di citazione più utilizzati in ambito accademico, ma differiscono per alcuni aspetti importanti. {introA} {introB} Comprendere queste differenze ti aiuterà a scegliere il formato giusto per il tuo elaborato.",
    styleIntro: "{style} ({fullName}) {tagline}.",
    styleIntroFallback:
      "{style} è uno stile di citazione diffuso, utilizzato in molte discipline accademiche.",
    tableHeading: "Confronto fianco a fianco",
    tableFeature: "Caratteristica",
    tableCitationSystem: "Sistema di citazione",
    tableInText: "Formato nel testo",
    tableReferenceList: "Nome dell'elenco dei riferimenti",
    tableEdition: "Edizione",
    tableCommonFields: "Ambiti comuni",
    // Title case, for the table cell. `styleLanding.system*` says "Author–date" for the
    // fact list on the style landing pages; both spellings are deliberate.
    systemAuthorDate: "Autore-Data",
    systemNumbered: "Numerico",
    systemFootnotes: "Note a piè di pagina",
    // The same three systems inside a sentence ("uses a numbered citation system").
    describeAuthorDate: "autore-data tra parentesi",
    describeNumbered: "numerico",
    describeFootnotes: "basato su note a piè di pagina",
    whenToUseHeading: "Quando usare {style}",
    whenToUseBody:
      "{style} ({fullName}) utilizza un sistema di citazione {system}, con citazioni nel testo in formato {inText} e {list} alla fine dell'elaborato. È utilizzato soprattutto in {fields}{more}.",
    whenToUseMoreFields: ", e ambiti correlati",
    whenToUseFallback:
      "{style} è uno stile di citazione ampiamente riconosciuto. Consulta le indicazioni del tuo elaborato o i requisiti della rivista di destinazione per verificare se {style} è il formato richiesto.",
    ctaHeading: "Guarda la differenza in azione",
    ctaBodyBefore: "Prova il nostro",
    ctaBodyLink: "strumento di confronto interattivo",
    ctaBodyAfter:
      "per confrontare le citazioni {a} e {b} fianco a fianco usando le tue fonti.",
    ctaCiteIn: "Cita in {style} →",
    faqDifferenceQ: "Qual è la differenza tra {a} e {b}?",
    faqDifferenceA:
      "{a} e {b} differiscono per il formato delle citazioni nel testo, la struttura dell'elenco dei riferimenti e le discipline accademiche che li utilizzano. {detail}",
    faqDifferenceDetail:
      "{a} utilizza citazioni nel testo in formato {inTextA} con {listA}, mentre {b} utilizza {inTextB} con {listB}.",
    faqDifferenceDetailFallback:
      "Ogni stile applica regole distinte per la formattazione delle citazioni, l'organizzazione delle bibliografie e la gestione dei diversi tipi di fonte.",
    faqBetterQ: "Quale dei due è migliore, {a} o {b}?",
    faqBetterA:
      "Né {a} né {b} è intrinsecamente migliore. La scelta giusta dipende dalla tua disciplina accademica, dai requisiti del tuo docente o dalla rivista a cui stai inviando il lavoro. Consulta le indicazioni del tuo elaborato o la pubblicazione di destinazione per stabilire quale stile utilizzare.",
    faqSwitchQ: "Posso passare da {a} a {b}?",
    faqSwitchA:
      "Sì, puoi passare da {a} a {b} usando il generatore di citazioni gratuito di CitationEasy. Basta selezionare lo stile desiderato e le citazioni verranno riformattate automaticamente. Tuttavia, dovresti usare un solo stile per elaborato, salvo indicazioni diverse del tuo docente.",
  },

  // The ~21 converter-pair pages at `/<locale>/convert/<slug>`.
  //
  // Only the shell is here; each pair's own four strings are in `converterCopy` below.
  // The format names (DOI, ISBN, BibTeX, RIS, CSL-JSON, PubMed ID, arXiv ID, Word) are
  // identifiers, never translated.
  converterPage: {
    notFoundTitle: "Convertitore non trovato",
    metaTitle: "{title} - Gratuito e istantaneo",
    ogTitle: "{title}",
    ogKind: "Convertitore",
    breadcrumbConverters: "Convertitori",
    breadcrumbLabel: "Percorso di navigazione",
    whenHeading: "Quando ti serve",
    relatedHeading: "Convertitori correlati",
    allConverters: "Tutti i convertitori",
    formatConverterTool: "Convertitore di formato citazioni",
    faqHowQ: "Come si converte {input} in {output}?",
    faqHowA:
      "{intro} Tutto è gratuito, senza account e senza limiti di conversione.",
    faqPrivacyQ: "I miei dati vengono caricati da qualche parte?",
    faqPrivacyIdentifier:
      "Ogni {input} viene cercato nel servizio pubblico di metadati corrispondente (Crossref per i DOI, PubMed per i PMID, i cataloghi delle biblioteche per gli ISBN), e viene inviato solo l'identificativo stesso — mai i tuoi documenti. I risultati formattati sono tuoi da copiare o scaricare; nulla viene memorizzato.",
    faqPrivacyLocal:
      "No — il {input} che incolli o carichi viene elaborato interamente nel tuo browser. {detail}",
    faqPrivacyDetailStyle:
      "Solo i campi bibliografici estratti (autore, titolo, anno) vengono inviati al nostro formattatore per generare le citazioni; il file in sé non lascia mai il tuo dispositivo.",
    faqPrivacyDetailOffline:
      "La conversione viene eseguita interamente offline nella pagina — nulla viene inviato ad alcun server.",
    faqUsefulQ: "Quando è utile una conversione da {input} a {output}?",
    faqOtherQ: "Posso convertire anche verso altri stili e formati?",
    faqOtherA:
      "Sì. Il generatore di CitationEasy formatta qualsiasi fonte in {count} stili di citazione, la bibliografia si esporta in Word, RTF, BibTeX, RIS e CSL-JSON, e l'hub dei convertitori elenca ogni coppia di conversione diretta. Se hai bisogno di riscrivere da uno stile all'altro un testo già formattato, usa invece lo strumento Convertitore di formato citazioni.",
  },

  // The 21 converter pairs' own copy (`CONVERTER_PAIRS` in src/data/converters.ts),
  // keyed by slug: title, meta description, the intro paragraph under the H1, and the
  // "when you need this" paragraph.
  //
  // Four hand-written strings per pair, and `src/data/converters.ts` has a quality rule
  // saying they must stay hand-written — no mail-merged sentences, because mass-templated
  // pages are what earned the site a scaled-content penalty. That rule is about how the
  // copy is authored; it says nothing about which language a reader gets it in. Left in
  // the data file they are the entire body of an otherwise translated page.
  // `converterPairCopy()` (src/lib/i18n/data-labels.ts) returns all four at once and
  // falls back to the data file per field, so a converter added upstream ships English
  // copy rather than a blank page.
  converterCopy: {
    doiToApaTitle: "Convertitore di citazioni da DOI a APA",
    doiToApaMetaDescription:
      "Incolla i DOI (uno per riga) e ottieni all'istante i riferimenti in APA 7, ricavati dai metadati ufficiali di Crossref. Gratis, senza account.",
    doiToApaIntro:
      "Un DOI punta già ai metadati ufficiali dell'editore, quindi non serve digitare un riferimento APA a mano. Incolla un DOI per riga e ognuno diventa un riferimento APA 7 completo — autori invertiti, titolo con la sola iniziale maiuscola, rivista in corsivo e il link https://doi.org/ richiesto da APA.",
    doiToApaUseCase:
      "Il momento tipico: il tuo docente vuole un elenco di riferimenti in APA, e tutto ciò che hai è il mucchio di DOI salvati durante le letture. Incolla il mucchio, copia l'elenco.",
    doiToMlaTitle: "Convertitore di citazioni da DOI a MLA",
    doiToMlaMetaDescription:
      "Trasforma i DOI in voci bibliografiche MLA 9 in automatico usando i metadati Crossref. Incolla un DOI per riga — gratis, niente da installare.",
    doiToMlaIntro:
      "L'MLA 9 tratta un articolo di rivista come una catena di contenitori — titolo dell'articolo tra virgolette, rivista in corsivo, volume, numero, anno, pagine — laboriosa da comporre a mano. Una ricerca per DOI riempie ogni campo a partire dal record dell'editore, così la voce bibliografica esce corretta al primo tentativo.",
    doiToMlaUseCase:
      "Utile quando un corso umanistico richiede l'MLA ma le tue fonti provengono da banche dati scientifiche che offrono solo DOI.",
    doiToBibtexTitle: "Convertitore da DOI a BibTeX",
    doiToBibtexMetaDescription:
      "Converti i DOI in voci BibTeX per LaTeX e Overleaf. Incolla un DOI per riga, scarica un .bib pronto — gratis e senza limiti.",
    doiToBibtexIntro:
      "Ogni elaborato in LaTeX finisce per avere bisogno del proprio file .bib. Incolla i DOI, uno per riga, e ottieni voci @article pulite con chiavi di citazione generate automaticamente, pronte per Overleaf, natbib o biblatex — senza copiare i campi a mano dalle pagine dell'editore.",
    doiToBibtexUseCase:
      "Il flusso classico: un revisore ti manda tre articoli da citare, hai i loro DOI e la scadenza è stasera. Incolla, scarica, \\cite e via.",
    doiToRisTitle: "Convertitore da DOI a RIS",
    doiToRisMetaDescription:
      "Converti i DOI in un file RIS che Zotero, Mendeley ed EndNote importano direttamente. Incolla i DOI uno per riga — gratis, senza registrazione.",
    doiToRisIntro:
      "RIS è il formato di interscambio che ogni gestionale di riferimenti sa importare. Incolla i DOI e scarica un file .ris i cui campi TY/AU/TI/T2 arrivano direttamente da Crossref — poi basta File → Importa in Zotero, Mendeley o EndNote e le voci arrivano già strutturate.",
    doiToRisUseCase:
      "Perfetto per trasferire un elenco di DOI raccolti a mano in un vero gestionale di riferimenti, senza riscrivere nemmeno un nome di autore.",
    isbnToApaTitle: "Convertitore di citazioni da ISBN a APA",
    isbnToApaMetaDescription:
      "Trasforma gli ISBN in riferimenti bibliografici di libri in APA 7 in automatico. Incolla un ISBN per riga e copia l'elenco formattato — gratis, senza account.",
    isbnToApaIntro:
      "L'ISBN nella pagina del colophon di un libro è tutto ciò che serve per il suo riferimento APA. Ogni ISBN viene cercato nei metadati bibliotecari e formattato in stile APA 7 — autore, anno, titolo in corsivo ed editore — comprese le informazioni sull'edizione quando il record le riporta.",
    isbnToApaUseCase:
      "Il modo più rapido per citare una pila di libri presi in biblioteca: apri ognuno al colophon, digita gli ISBN, incolla l'elenco qui.",
    isbnToMlaTitle: "Convertitore di citazioni da ISBN a MLA",
    isbnToMlaMetaDescription:
      "Converti gli ISBN in voci bibliografiche MLA 9 con un solo incolla. Ricerca ISBN gratuita con output già formattato — senza registrazione.",
    isbnToMlaIntro:
      "Le voci di libro in MLA 9 sono brevi — autore, titolo in corsivo, editore, anno — ma l'inversione dell'autore e la punteggiatura continuano a creare problemi. Una ricerca per ISBN restituisce il vero record di catalogo del libro e lo formatta secondo il modello degli elementi essenziali del manuale MLA.",
    isbnToMlaUseCase:
      "Ideale per i saggi di letteratura: cita ogni romanzo e ogni monografia critica sulla tua scrivania digitando solo i numeri sul retro di copertina.",
    isbnToBibtexTitle: "Convertitore da ISBN a BibTeX",
    isbnToBibtexMetaDescription:
      "Converti gli ISBN in voci BibTeX @book per LaTeX. Incolla un ISBN per riga, scarica il file .bib — gratis e istantaneo.",
    isbnToBibtexIntro:
      "I libri sono l'angolo scomodo di BibTeX — nessun DOI da cercare, e le pagine degli editori raramente offrono un export .bib. Questo convertitore risolve ogni ISBN nei metadati bibliotecari e genera voci @book con i campi di autore, titolo, editore e anno già compilati.",
    isbnToBibtexUseCase:
      "Per chi scrive una tesi: i capitoli di metodologia citano DOI di articoli, ma il capitolo teorico cita dodici libri. Questo se ne occupa.",
    pmidToApaTitle: "Convertitore di citazioni da PMID a APA",
    pmidToApaMetaDescription:
      "Converti gli ID di PubMed in riferimenti APA 7 in automatico. Incolla i PMID uno per riga e ottieni un elenco di riferimenti formattato — gratis.",
    pmidToApaIntro:
      "Ogni record di PubMed ha un PMID, e i corsi di psicologia e infermieristica richiedono quasi sempre l'APA. Incolla i PMID uno per riga e ognuno viene risolto tramite i metadati di PubMed in un riferimento di rivista APA 7 completo, con il link del DOI incluso quando il record ne ha uno.",
    pmidToApaUseCase:
      "Pensato per gli elaborati di pratica basata sulle evidenze: la tua strategia di ricerca finisce con un elenco di PMID, e il tuo lavoro li richiede in APA.",
    pmidToRisTitle: "Convertitore da PMID a RIS",
    pmidToRisMetaDescription:
      "Trasforma gli ID di PubMed in un file RIS per Zotero, Mendeley o EndNote. Incolla i PMID uno per riga, scarica il .ris — gratis.",
    pmidToRisIntro:
      "I flussi di lavoro per le revisioni sistematiche vivono sui file RIS. Incolla i PMID della tua ricerca su PubMed e scarica un .ris pronto per il tuo strumento di screening o gestionale di riferimenti — ogni record strutturato a partire dai metadati canonici di PubMed anziché da testo estratto.",
    pmidToRisUseCase:
      "Utile a metà revisione, quando un collega ti scrive 'includi anche questi cinque studi' come semplici PMID, e la tua libreria vive in EndNote.",
    arxivToBibtexTitle: "Convertitore da arXiv a BibTeX",
    arxivToBibtexMetaDescription:
      "Converti gli ID di arXiv in voci BibTeX per gli elaborati in LaTeX. Incolla ID come 2301.12345, uno per riga, e scarica il .bib — gratis.",
    arxivToBibtexIntro:
      "I preprint vengono citati di continuo negli ambiti che si muovono velocemente, e gli ID di arXiv (come 2301.12345) sono il modo in cui circolano. Incolla gli ID e ottieni voci BibTeX con autori, titolo, anno e link arXiv del preprint — pronte per il file .bib del tuo elaborato.",
    arxivToBibtexUseCase:
      "Per chi scrive in machine learning o fisica, la cui sezione sui lavori correlati è per l'80% arXiv: raccogli gli ID mentre leggi, convertili tutti insieme al momento della scrittura.",
    bibtexToApaTitle: "Convertitore da BibTeX a APA",
    bibtexToApaMetaDescription:
      "Incolla o carica un file .bib e ottieni un elenco di riferimenti in APA 7. Gestisce file con più voci — gratis, l'elaborazione avviene nel tuo browser.",
    bibtexToApaIntro:
      "A volte l'elaborato lascia LaTeX: una rivista vuole un invio in Word, oppure una commissione vuole l'elenco dei riferimenti in testo semplice. Incolla il tuo BibTeX (o carica il .bib) e ogni voce viene elaborata nel tuo browser, poi formattata come un elenco di riferimenti APA 7 da inserire in qualsiasi documento.",
    bibtexToApaUseCase:
      "Il caso tipico delle domande di finanziamento: il tuo elenco di pubblicazioni vive in un file .bib, e il modulo dell'ente finanziatore vuole i riferimenti in formato APA in una casella di testo.",
    bibtexToMlaTitle: "Convertitore da BibTeX a MLA",
    bibtexToMlaMetaDescription:
      "Converti BibTeX in voci bibliografiche MLA 9. Incolla o carica il file .bib e copia l'elenco formattato — gratis, senza account.",
    bibtexToMlaIntro:
      "BibTeX conserva i campi grezzi; l'MLA stabilisce esattamente come disporli. Questo convertitore elabora le tue voci .bib in locale e restituisce ognuna secondo il modello MLA 9 — inversione dell'autore, titoli di articolo tra virgolette, contenitori in corsivo e la punteggiatura corretta tra gli elementi.",
    bibtexToMlaUseCase:
      "Per chi si muove tra discipline diverse: i tuoi riferimenti vivono in BibTeX da un vecchio lavoro in LaTeX, ma la rivista umanistica a cui stai inviando l'elaborato vuole l'MLA.",
    bibtexToRisTitle: "Convertitore da BibTeX a RIS",
    bibtexToRisMetaDescription:
      "Converti un file .bib in RIS per Zotero, Mendeley o EndNote. Incolla o carica il BibTeX, scarica il .ris — gratis e offline.",
    bibtexToRisIntro:
      "Passare dagli strumenti LaTeX a un gestionale di riferimenti significa tradurre le voci @article/@book di BibTeX nei tag TY/AU/TI di RIS. La conversione avviene interamente nel tuo browser — mappature dei campi incluse — e ti consegna un file .ris che qualsiasi gestionale importa.",
    bibtexToRisUseCase:
      "Innesco tipico: un nuovo coautore lavora con Zotero, i riferimenti condivisi sono un .bib nel repository, e qualcuno deve fare da ponte.",
    bibtexToCslJsonTitle: "Convertitore da BibTeX a CSL-JSON",
    bibtexToCslJsonMetaDescription:
      "Converti BibTeX in CSL-JSON per Pandoc e Quarto. Incolla o carica il .bib, scarica references.json — gratis, nel browser.",
    bibtexToCslJsonIntro:
      "Pandoc e Quarto citano nativamente da CSL-JSON, e convertire il tuo .bib esistente elimina un ingranaggio dalla build. Le voci vengono elaborate nel tuo browser e restituite come un array CSL-JSON pulito — esattamente la forma che --bibliography=references.json si aspetta.",
    bibtexToCslJsonUseCase:
      "Per chi lavora prima in Markdown e sta convertendo un vecchio progetto LaTeX in Quarto: porta con te la bibliografia senza modificare il JSON a mano.",
    bibtexToWordTitle: "Convertitore da BibTeX a Word",
    bibtexToWordMetaDescription:
      "Trasforma un file .bib in un documento Word con un elenco di riferimenti in APA e rientro sporgente. Gratis — l'elaborazione avviene nel tuo browser.",
    bibtexToWordIntro:
      "Quando un file .bib deve diventare un documento — e non un altro formato di dati — questo convertitore elabora il tuo BibTeX in locale, formatta ogni voce in APA 7 e scarica un file Word con l'elenco dei riferimenti già in ordine alfabetico e a rientro sporgente.",
    bibtexToWordUseCase:
      "La mossa di fine percorso: la rivista ha accettato l'elaborato ma vuole l'elenco finale dei riferimenti come file Word modificabile, e la tua fonte di verità è BibTeX.",
    risToApaTitle: "Convertitore da RIS a APA",
    risToApaMetaDescription:
      "Incolla o carica un export RIS e ottieni un elenco di riferimenti in APA 7. Funziona con Zotero, Mendeley, EndNote ed export da banche dati — gratis.",
    risToApaIntro:
      "Il pulsante Esporta di qualsiasi banca dati produce RIS; gli elaborati vogliono l'APA. Incolla l'export (o carica il .ris) e ogni record TY/AU/TI viene elaborato nel tuo browser e reso come riferimento APA 7 — senza dover installare alcun gestionale di riferimenti.",
    risToApaUseCase:
      "La scorciatoia delle banche dati bibliotecarie: esporta i risultati della ricerca come RIS, converti qui e salta la configurazione di Zotero per un lavoro occasionale.",
    risToMlaTitle: "Convertitore da RIS a MLA",
    risToMlaMetaDescription:
      "Converti gli export RIS da banche dati o gestionali di riferimenti in voci bibliografiche MLA 9. Incolla o carica — gratis, senza registrazione.",
    risToMlaIntro:
      "JSTOR, ProQuest ed EBSCO esportano tutti in RIS, e i dipartimenti umanistici vogliono tutti l'MLA. Questa pagina colma il divario: i record vengono elaborati in locale e resi secondo il modello degli elementi essenziali dell'MLA 9, pronti da incollare nella tua pagina di opere citate.",
    risToMlaUseCase:
      "Pensato per la notte prima della consegna del saggio: dieci fonti esportate da JSTOR come citations.ris, e nessuna voglia di formattarle a mano.",
    risToBibtexTitle: "Convertitore da RIS a BibTeX",
    risToBibtexMetaDescription:
      "Converti RIS in BibTeX per LaTeX e Overleaf. Incolla o carica l'export .ris, scarica un file .bib pulito — gratis, nel browser.",
    risToBibtexIntro:
      "In direzione LaTeX: gestionali di riferimenti e banche dati parlano RIS, mentre la build del tuo elaborato ha bisogno di .bib. I record vengono convertiti interamente nel tuo browser — tipi mappati dai tag TY, chiavi di citazione generate — in un file che Overleaf accetta così com'è.",
    risToBibtexUseCase:
      "Comune nelle collaborazioni: chi usa Zotero esporta RIS, chi usa LaTeX lo fa passare da questa pagina, e ognuno mantiene il proprio strumento.",
    risToCslJsonTitle: "Convertitore da RIS a CSL-JSON",
    risToCslJsonMetaDescription:
      "Converti gli export RIS in CSL-JSON per i flussi Pandoc e Quarto. Incolla o carica il .ris, scarica references.json — gratis.",
    risToCslJsonIntro:
      "Per chi usa Pandoc e Quarto, il CSL-JSON è il formato bibliografico nativo. Fornisci a questa pagina il RIS esportato dalla tua banca dati o dal tuo gestionale e ricevi un array JSON strutturato — ogni record elaborato in locale, senza inviare nulla da nessuna parte.",
    risToCslJsonUseCase:
      "Si inserisce nei flussi docs-as-code: i revisori inviano allegati RIS, il file delle citazioni del tuo repository è references.json, e questa pagina è l'adattatore.",
    cslJsonToBibtexTitle: "Convertitore da CSL-JSON a BibTeX",
    cslJsonToBibtexMetaDescription:
      "Converti CSL-JSON (export di Pandoc, Quarto o Zotero) in BibTeX. Incolla o carica il .json, scarica il .bib — gratis, nel browser.",
    cslJsonToBibtexIntro:
      "A volte il flusso si inverte: una bibliografia nata in una pipeline Pandoc o Zotero deve alimentare una build LaTeX. Questo convertitore legge l'array CSL-JSON nel tuo browser e genera voci BibTeX con tipi e campi rimappati a partire dal modello CSL.",
    cslJsonToBibtexUseCase:
      "Per l'autore che lavora in Quarto ma ha un coautore che insiste su Overleaf: mantieni references.json come fonte canonica e genera il .bib su richiesta.",
    cslJsonToRisTitle: "Convertitore da CSL-JSON a RIS",
    cslJsonToRisMetaDescription:
      "Converti CSL-JSON in RIS così che Zotero, Mendeley o EndNote possano importarlo. Incolla o carica il .json, scarica il .ris — gratis.",
    cslJsonToRisIntro:
      "Il CSL-JSON è meraviglioso all'interno delle pipeline Pandoc e oscuro per le finestre di importazione della maggior parte dei gestionali di riferimenti. Convertirlo in RIS rende gli stessi record universalmente importabili — ogni elemento mappato sui tag TY/AU/TI nel tuo browser, senza inviare nulla a un server.",
    cslJsonToRisUseCase:
      "Il problema del passaggio di consegne: la tua bibliografia è un references.json in un repository git, e il tuo relatore la vuole 'in EndNote' entro venerdì.",
  },

  // The infinite-scroll style list shared by the country and subject pages and by the
  // /citation-styles hub. It is a client component, so these arrive as a prop rather
  // than through `getDictionary` — pulling all 17 dictionaries into the browser bundle
  // to label a search box is the thing `link-path.ts` exists to avoid.
  stylesBrowser: {
    defaultTitle: "Tutti gli stili di citazione",
    styleSingular: "stile",
    stylePlural: "stili",
    found: "per questa ricerca",
    filterPlaceholder: "Filtra gli stili…",
    noMatches: "Nessuno stile corrisponde alla tua ricerca.",
    loadingMore: "Caricamento di altri stili…",
    loadMore: "Carica altri ({count} in più)",
    showingAll: "Visualizzazione di tutti i {count} stili",
    internationalLabel: "Internazionale",
  },

  footer: {
    tagline:
      "Un generatore di citazioni gratuito con {count} stili. Nessun account, nessun abbonamento a pagamento, nessun limite.",
    freeTools: "Strumenti gratuiti",
    resources: "Risorse",
    legal: "Note legali",
    language: "Lingua",
    languageLabel: "Scegli la tua lingua",
    rights:
      "Le citazioni sono formattate con le definizioni ufficiali del Citation Style Language.",
    popularStyles: "Stili più usati",
    about: "Chi siamo",
    privacy: "Privacy",
    terms: "Termini",
    contact: "Contatti",
    copyright: "© {year} CitationEasy. Uso gratuito.",
    poweredBy: "Basato su",
    allTools: "Tutti gli strumenti di scrittura",
    citationGuides: "Guide alla citazione",
    allStyles: "Tutti gli stili di citazione",
    citationGlossary: "Glossario della citazione",
    howToCiteAny: "Come citare qualsiasi fonte",
  },

  faq: {
    heading: "Domande frequenti",
    homeQ1: "CitationEasy è davvero gratuito?",
    homeA1:
      "Sì. CitationEasy è completamente gratuito: nessuna registrazione, nessun limite e nessuna versione a pagamento. Tutti i {count} stili di citazione sono a disposizione di chiunque.",
    homeQ2: "Quali stili di citazione sono supportati?",
    homeA2:
      "Tutti i {count} stili del progetto Citation Style Language, tra cui APA 7ª edizione, MLA 9ª edizione, Chicago, Harvard, Vancouver, IEEE, AMA e Turabian.",
    homeQ3: "Serve un account?",
    homeA3:
      "No. CitationEasy funziona interamente nel browser e la bibliografia resta salvata sul tuo dispositivo. Non viene caricato nulla e non c'è nessuna registrazione.",
    homeQ4: "Posso generare le citazioni nella mia lingua?",
    homeA4:
      "Sì. La lingua delle citazioni è un'impostazione separata da quella del sito: puoi lavorare nella tua lingua e produrre riferimenti nella lingua in cui è scritto il tuo elaborato.",
    styleQ1: "Il generatore di citazioni {style} è gratuito?",
    styleA1:
      "Sì. Generare citazioni {style} è gratuito e illimitato, senza account. Incolla un DOI, un URL, un ISBN o una voce BibTeX e copia il riferimento finito.",
    styleQ2: "Come si cita una fonte in {style}?",
    styleA2:
      "Incolla il DOI, l'URL, l'ISBN o la voce BibTeX della fonte nel generatore di questa pagina. Lo stile {style} è già selezionato, quindi il riferimento e la citazione nel testo compaiono subito.",
    styleQ3: "Quanto è accurata la formattazione {style}?",
    styleA3:
      "Ogni riferimento è costruito a partire dalla definizione ufficiale di {style} pubblicata dal progetto Citation Style Language — lo stesso file usato da Zotero e Mendeley — e non da un'approssimazione delle regole.",
  },

  tool: {
    modeLabel: "Modalità di inserimento della citazione",
    modeAuto: "URL / DOI / ISBN",
    modeBook: "Cerca libri",
    modeArticles: "Cerca articoli",
    modeManual: "Inserimento manuale",
    errorEnterManually: "Inserisci i dati manualmente",
    autoLabel: "Incolla un URL, DOI, PMID, ID arXiv, ISBN o BibTeX",
    autoHint:
      "Rileviamo il tipo automaticamente e recuperiamo i metadati. Incolla un identificatore per riga per citare un intero elenco in una volta.",
    generate: "Genera citazione",
    generating: "Generazione…",
    bookSearchLabel: "Cerca per titolo, autore o ISBN",
    bookSearchEmpty:
      "Ancora nessun risultato. Fai clic su Cerca per trovare libri.",
    bookSearchSource: "Cerca nel catalogo di Open Library, milioni di libri",
    worksSearchLabel: "Cerca per titolo dell’articolo o autori",
    worksSearchEmpty:
      "Ancora nessun risultato. Fai clic su Cerca per trovare articoli.",
    worksSearchSource:
      "Cerca nel catalogo Crossref: articoli di riviste, atti di convegno e capitoli di libro",
    showingResults: "Risultati {shown} di {total}",
    search: "Cerca",
    searching: "Ricerca…",
    bulkResults: "Risultati della citazione in blocco",
    copy: "Copia",
    copied: "Copiato!",
    copyHint: "Copia e incolla nel tuo elaborato",
    resultTitle: "Citazione formattata",
    edit: "Modifica",
    completeFields: "Completa",
    fieldsIncomplete:
      "one:{count} campo incompleto|other:{count} campi incompleti",
    outputLanguage: "Lingua",
    sourceType: "Tipo di fonte",
    fieldAuthors: "Autore/i",
    fieldAuthorsHint: "Rossi, Mario o Mario Rossi",
    fieldTitle: "Titolo dell’articolo o del libro",
    fieldBookTitle: "Titolo del libro",
    fieldBookTitleHint: "Titolo del libro o dell’antologia",
    fieldEditors: "Curatore/i",
    fieldJournal: "Rivista / pubblicazione",
    fieldJournalHint: "Nome della rivista o pubblicazione",
    fieldPublisher: "Editore / sito",
    fieldPublisherHint: "Nome dell’editore o sito web",
    fieldDate: "Data / anno",
    fieldVolume: "Volume",
    fieldIssue: "Fascicolo",
    fieldPages: "Pagina/e",
    fieldFormat: "Formato / supporto",
    fieldAuthorsGroup: "Autore collettivo od organizzazione",
    fieldAuthorsGroupHint:
      "Per indicare un gruppo insieme a persone, racchiudilo tra parentesi graffe: {R Core Team}, Mario Rossi",
    fieldTranslators: "Traduttore/i",
    fieldInterviewers: "Intervistatore/i",
    fieldVersion: "Versione",
    fieldCourt: "Tribunale",
    fieldIssuingAuthority: "Autorità di rilascio",
    fieldPatentNumber: "Numero di brevetto",
    fieldEpisodeNumber: "Numero dell’episodio",
    fieldLawNumber: "Numero della legge o del disegno di legge",
    fieldReportNumber: "Numero del rapporto",
    fieldReviewedTitle: "Titolo dell’opera recensita",
    fieldReviewedAuthors: "Autore/i dell’opera recensita",
    inTextCitation: "Citazione nel testo",
    quickEdit: "Modifica rapida",
    quickEditHint: "Aggiorna i metadati e rigenera la citazione.",
    update: "Aggiorna",
    saving: "Salvataggio…",
    fieldAuthor: "Autore",
    fieldDateShort: "Data",
    fieldTitleShort: "Titolo",
  },

  toolCollections: {
    "citation-generators": "Generatori di citazioni",
    "citation-tools": "Strumenti per citazioni e bibliografie",
    "paper-formatting": "Formattazione dei lavori",
    "essay-writing": "Scrittura di saggi e tesine",
    "writing-analysis": "Analisi e revisione del testo",
    "word-counters": "Contatori e leggibilità",
    vocabulary: "Vocabolario e ricerca di parole",
    "research-tools": "Ricerca e pubblicazione",
    statistics: "Calcolatori statistici",
    math: "Calcolatori di matematica",
    science: "Calcolatori di scienze e laboratorio",
    charts: "Grafici e diagrammi",
    economics: "Economia, impresa e finanza",
    grades: "Calcolatori di GPA e voti",
    study: "Studio e produttività",
    classroom: "Classe e materiali stampabili",
    random: "Generatori e sorteggi casuali",
    "text-tools": "Pulizia e formattazione del testo",
    "fancy-text": "Testo decorativo e simboli",
    language: "Lingue e traslitterazione",
    ciphers: "Codici e cifrari",
    converters: "Convertitori di unità, date e numeri",
    developer: "Dati, markup e codice",
  },
  bib: {
    title: "Le mie bibliografie",
    studioTitle: "Studio bibliografico",
    studioProjects: "Progetti",
    studioExplore: "Esplora",
    studioExploreIntro:
      "Guide, strumenti e tutti gli stili di citazione a un clic.",
    studioStyleGuide: "Guida allo stile {style}",
    studioStyleGuideHint:
      "Regole, esempi svolti e formato della citazione nel testo",
    studioGenerator: "Generatore di citazioni",
    studioNewProject: "Nuovo progetto",
    studioSwitchProject: "Cambia progetto",
    studioAddSource: "Aggiungi una fonte",
    studioAddSourceHint:
      "Incolla un DOI, un URL o un ISBN, cerca libri e articoli oppure inserisci i dati a mano. Tutto ciò che salvi finisce in questo progetto.",
    studioDoneAdding: "Fine",
    studioReferences: "Riferimenti",
    studioReferenceCount: "{count} riferimenti",
    studioInspectorTitle: "Dettagli del riferimento",
    studioInspectorEmpty:
      "Seleziona un riferimento per vedere la citazione nel testo, le note e i dettagli della fonte.",
    studioSavedLocally: "Salvato in questo browser",
    studioOpen: "Apri lo Studio bibliografico",
    studioViewInStudio: "Vedi nello Studio",
    studioFooterSaved: "{count} salvate nel tuo Studio bibliografico",
    studioFooterEmpty:
      "Le citazioni salvate formano una bibliografia completa nello Studio",
    studioClearSelection: "Annulla selezione",
    studioDeleteSelected: "Elimina selezionate",
    studioDeleteSelectedBody:
      "Eliminare {count} citazioni da “{name}”? L’operazione non può essere annullata.",
    studioDeletedMany: "{count} citazioni eliminate",
    studioMoveTo: "Sposta in",
    studioCopyTo: "Copia in",
    studioSearchShortcut: "Premi / per cercare",
    studioStyle: "Stile",
    studioCopyEntry: "Copia voce",
    studioEntryCopied: "Voce copiata",
    studioSourceDetails: "Dettagli della fonte",
    studioEmptyTitle: "Questo progetto è vuoto",
    studioEmptyBody:
      "Aggiungi la tua prima fonte qui sopra oppure importa una libreria BibTeX, RIS o CSL-JSON esistente.",
    studioBackup: "Backup",
    sharedTitle: "Bibliografia condivisa",
    save: "Salva",
    saving: "Salvataggio…",
    saved: "Salvato",
    searchPlaceholder: "Cerca tra le citazioni…",
    empty: "Nessuna citazione salvata",
    emptySearch: "Nessuna citazione corrisponde alla ricerca.",
    countIn: "Citazioni in {name}: {count}",
    savedOn: "Salvata il {date}",
    emptyInBib:
      "Non ci sono ancora citazioni in questa bibliografia. Genera e salva citazioni, oppure importa una libreria BibTeX, RIS o CSL-JSON.",
    savedToast: "Citazione salvata nella bibliografia",
    copyAll: "Copia tutto",
    copyAllTitle: "Copia tutte le citazioni",
    export: "Esporta",
    exportTitle: "Esporta la bibliografia",
    import: "Importa",
    importTitle: "Importa riferimenti",
    importLabel: "Importa riferimenti (BibTeX, RIS o CSL-JSON)",
    importing: "Importazione…",
    importFailed: "Importazione non riuscita. Riprova.",
    importSummary:
      "Importate: {added} · Duplicati ignorati: {duplicates} · Oltre il limite: {overLimit} · Errori: {failed}",
    importUnreadable: "Impossibile leggere il file.",
    importUnparseable: "Impossibile importare i riferimenti.",
    importReviewTitle: "Rivedi importazione",
    importReviewIntro:
      "Rivedi {total} riferimenti {format} prima di formattarli e salvarli.",
    importReviewCount: "{included} di {total} inclusi",
    importSelectAll: "Includi tutti",
    importExcludeAll: "Escludi tutti",
    importLimitNotice:
      "{count} riferimenti oltre il limite di 200 voci non saranno importati.",
    importDuplicateExisting: "Possibile duplicato di “{title}”, già salvato",
    importDuplicateBatch:
      "Possibile duplicato di “{title}” in questa importazione",
    importMissingField: "{field} mancante",
    importUntitled: "Riferimento senza titolo",
    importReview: "Rivedi riferimenti",
    importCommit: "Formatta e importa",
    importBack: "Indietro",
    importOutcomeTitle: "Importazione completata",
    importOutcomeSummary:
      "Aggiunti: {added} · Duplicati ignorati: {duplicates} · Esclusi: {excluded} · Oltre il limite: {overLimit} · Errori: {failed}",
    sortBy: "Ordina per",
    sortTitle: "Ordina la bibliografia",
    sortAuthor: "Autore (A–Z)",
    sortNewest: "Data di aggiunta (più recente)",
    sortOldest: "Anno (dal più vecchio)",
    sortManual: "Ordine manuale",
    backupAll: "Backup completo dei progetti (.json)",
    backupDownloaded: "Backup completo scaricato",
    backupUnreadable: "Impossibile leggere il file di backup.",
    restore: "Ripristina",
    restoreTitle: "Ripristina tutti i progetti bibliografici",
    restoreWarning:
      "Il backup verrà prima convalidato, poi sostituirà tutte le bibliografie salvate in questo browser. L’operazione non può essere annullata.",
    restoreChoose: "Scegli un backup CitationEasy (.json)",
    replaceAndRestore: "Sostituisci e ripristina",
    restoreComplete: "Tutti i progetti bibliografici sono stati ripristinati",
    restoreInvalid: "Questo non è un backup di progetto CitationEasy valido.",
    selectAllVisible: "Seleziona tutti gli elementi mostrati",
    selectedCount: "{count} selezionate",
    clusterOpen: "Cita insieme",
    clusterTitle: "Citazione composita nel testo",
    clusterDescription:
      "Crea un unico gruppo di citazioni dai riferimenti selezionati. Lo stile scelto controlla ordine, punteggiatura e numerazione.",
    clusterTarget: "Output",
    clusterLocatorOptional: "Localizzatore facoltativo",
    clusterBuildCopy: "Crea e copia la citazione",
    clusterBuilding: "Creazione della citazione…",
    clusterCopied: "Citazione composita copiata",
    clusterFailed: "Impossibile creare la citazione composita. Riprova.",
    destination: "Bibliografia di destinazione",
    chooseDestination: "Scegli una bibliografia…",
    copySelected: "Copia",
    moveSelected: "Sposta",
    copyComplete: "{count} citazioni copiate. {skipped} duplicati ignorati.",
    moveComplete:
      "{count} citazioni spostate. {skipped} duplicati sono rimasti nell’origine.",
    selectCitation: "Seleziona {title}",
    moveUp: "Sposta su",
    moveDown: "Sposta giù",
    deleteTitle: "Elimina citazione",
    deleteConfirm: "Eliminare la citazione?",
    deleted: "Citazione eliminata",
    editTitle: "Modifica citazione",
    close: "Chiudi",
    copiedAll: "Tutte le citazioni copiate!",
    copiedAnnotated: "Bibliografia ragionata copiata!",
    copiedBibtex: "BibTeX copiato!",
    copiedBulleted: "Elenco puntato copiato!",
    copiedNumbered: "Elenco numerato copiato!",
    copiedHtml: "Frammento HTML copiato!",
    downloadedWord: "Documento Word scaricato",
    downloadedRtf: "File RTF scaricato",
    downloadedRis: "File RIS scaricato",
    downloadedCsl: "File CSL-JSON scaricato",
    downloadedAnnotated: "Bibliografia ragionata scaricata",
    copyFailed: "Copia negli appunti non riuscita",
    shareCopied:
      "Link copiato! Chiunque lo abbia può vedere e importare questo elenco.",
    shareFailed: "Impossibile creare il link di condivisione",
    regenerateFailed: "Impossibile rigenerare la citazione",
    updateFailed: "Impossibile aggiornare la citazione",
    fieldAuthor: "Autore",
    fieldTitle: "Titolo",
    fieldDate: "Data",
    fieldPublisher: "Editore",
    fieldVolume: "Volume",
    fieldIssue: "Fascicolo",
    fieldPages: "Pagina/e",
    fieldUrl: "URL",
    fieldAccessed: "Data di consultazione",
    fieldEditor: "Curatore",
    fieldEvent: "Luogo dell’evento",
    noteLabel: "Annotazione su questa fonte",
    noteEdit: "Modifica questa nota",
    notePlaceholder:
      "Riassumi la fonte, valutala e spiega come si inserisce nel tuo lavoro…",
    noteRemoved: "Nota rimossa",
    noteSave: "Salva nota",
    noteCancel: "Annulla",
    noteAdd: "Aggiungi nota",
    noteWords: "{count} parole",
    noteSaved: "Nota salvata ({count} parole)",
    noteClickToEdit: "fai clic per modificare",
    inTextCopy: "Copia la citazione nel testo per questa fonte",
    inTextSelect: "Seleziona la citazione qui sotto per copiarla.",
    inTextFailed: "Impossibile costruire la citazione nel testo. Riprova.",
    locator: "Riferimento puntuale",
    locatorType: "Tipo di riferimento",
    locatorPage: "Pagina",
    locatorChapter: "Capitolo",
    locatorSection: "Sezione",
    locatorParagraph: "Paragrafo",
    locatorLine: "Riga",
    locatorVerse: "Versetto",
    reformat: "Riformatta",
    reformatTitle: "Riformatta tutte le citazioni in un altro stile",
    reformatHeading: "Riformatta tutto in un solo stile",
    reformatChoose: "Scegli uno stile…",
    reformatFailed:
      "Riformattazione non riuscita. La bibliografia è invariata.",
    sortOrder: "Criterio di ordinamento",
    copyToClipboard: "Copia negli appunti",
    numberedList: "Elenco numerato",
    bulletedList: "Elenco puntato",
    htmlSnippet: "Frammento HTML",
    copyWithAnnotations: "Copia con le annotazioni",
    wordAnnotated: "Documento Word con annotazioni (.doc)",
    downloadFile: "Scarica il file",
    wordDocument: "Documento Word (.doc)",
    copyShareLink: "Copia il link di condivisione",
    print: "Stampa…",
    printFailed: "Popup bloccato — consenti i popup per stampare",
    font: "Carattere",
    sitePublication: "Sito / nome della pubblicazione",
    importIntro:
      "Incolla o carica un file BibTeX (.bib), RIS (.ris) o CSL-JSON (.json)",
    importUpload: "Carica un file .bib, .ris o .json",
    importPaste: "Oppure incolla BibTeX, RIS o CSL-JSON",
    cancel: "Annulla",
    done: "Fine",
    delete: "Elimina",
    deleteBody:
      "Questa citazione verrà rimossa definitivamente dalla tua bibliografia.",
    editBody: "Aggiorna i metadati e salva per rigenerare la citazione.",
    reformatExplain:
      "Ricostruisce tutte le {count} voci dai dati salvati: non viene riscaricato nulla e nessuna fonte va persa.",
    inTextHeading: "Citazione nel testo",
    inTextLocatorHint:
      "Aggiungi una pagina o un altro riferimento per ancorare la citazione a un punto della fonte: la maggior parte degli stili lo richiede nelle citazioni dirette. Lascia vuoto per la forma semplice.",
    inTextCopyButton: "Copia la citazione nel testo",
    mixedStyles:
      "Questa bibliografia mescola {count} stili di citazione ({styles}).",
    mixedStylesAction:
      "Usa {action} per uniformarli tutti in un solo stile prima di consegnarla.",
    selectBibliography: "Seleziona bibliografia",
    newBibliography: "Nuova bibliografia",
    moreOptions: "Altre opzioni",
    rename: "Rinomina",
    removeDuplicates: "Rimuovi duplicati",
    cannotDeleteOnly: "Non puoi eliminare la tua unica bibliografia",
    newBibliographyBody:
      "Crea una bibliografia per un altro elaborato o progetto.",
    bibliographyName: "Nome della bibliografia",
    create: "Crea",
    renameBibliography: "Rinomina bibliografia",
    renameBibliographyBody: "Inserisci un nuovo nome per “{name}”.",
    newName: "Nuovo nome",
    duplicateRemovedOne: "Rimosso {count} duplicato",
    duplicateRemovedMany: "Rimossi {count} duplicati",
    noDuplicates: "Nessun riferimento duplicato trovato",
    removeDuplicatesConfirm: "Rimuovere i riferimenti duplicati?",
    removeDuplicatesBody:
      "Questa azione cerca in “{name}” la stessa fonte (tramite DOI, ISBN o titolo, anno e autore) e ne conserva una copia. I riferimenti non confrontabili restano invariati.",
    deleteBibliographyConfirm: "Eliminare la bibliografia?",
    deleteBibliographyBody:
      "Questa azione eliminerà definitivamente “{name}” e tutte le sue {count} citazioni.",
  },

  bibliographyPage: {
    metaTitle: "Studio bibliografico - organizza, annota ed esporta citazioni",
    metaDescription:
      "Crea e gestisci progetti bibliografici privati nel browser. Cerca, ordina, annota, modifica, riformatta, importa, esporta e condividi citazioni senza account.",
    keyword1: "creatore di bibliografie",
    keyword2: "gestore di citazioni",
    keyword3: "strumento per bibliografia annotata",
    ogTitle: "Studio bibliografico",
    ogDescription:
      "Uno spazio privato a pagina intera per organizzare, formattare, annotare, importare ed esportare citazioni.",
    ogImageTitle: "Studio bibliografico",
    ogImageSubtitle:
      "Organizza, annota, riformatta, esporta e condividi i tuoi riferimenti.",
    ogKind: "Spazio per le citazioni",
    breadcrumb: "Studio bibliografico",
    eyebrow: "Spazio privato nel browser",
    lead: "Gestisci tutti i riferimenti di un elaborato in un solo posto. I progetti restano in questo browser, a meno che tu non scelga di esportarli o condividerli.",
    privacyTitle: "Privato per impostazione predefinita",
    privacyBody:
      "Progetti e annotazioni usano l’archiviazione locale del browser. I link condivisi includono le citazioni ma escludono intenzionalmente le note private.",
    workflowTitle: "Dall’elenco delle fonti all’elaborato finale",
    workflowBody:
      "Cambia progetto, cerca e ordina i riferimenti, aggiungi annotazioni o localizzatori, correggi i metadati e riformatta l’intero elenco in un solo stile.",
    formatsTitle: "Importa i riferimenti e portali ovunque",
    formatsBody:
      "Importa BibTeX, RIS o CSL-JSON. Copia elenchi formattati o esporta Word, RTF, RIS, CSL-JSON, HTML, BibTeX e versioni annotate.",
  },

  // The 95-term glossary: /glossary and /glossary/[slug].
  //
  // The chrome only. Each term's own prose — `short`, the `body` paragraphs, the
  // example — is ~10,600 words that cannot live in a flat string map and must not be
  // copied verbatim into sixteen files; it lives in `src/content/glossary/<locale>.json`
  // and is read through `lib/localized-content.ts`.
  glossaryPage: {
    metaTitle: "{term} - definizione ed esempi",
    notFoundTitle: "Termine non trovato",
    keywordDefinition: "definizione di {term}",
    keywordWhatIs: "cos'è {term}",
    ogTitle: "{term} - Glossario di CitationEasy",
    ogKind: "Glossario delle citazioni",
    breadcrumb: "Glossario",
    alsoCalled: "Chiamato anche: {aliases}",
    doItHeading: "Fallo su CitationEasy",
    seeAlsoHeading: "Vedi anche",
    moreInHeading: "Altro in {category}",
    navLabel: "Navigazione del glossario",
    allTermsLink: "Tutti i {count} termini del glossario",
    generatorLink: "Generatore di citazioni gratuito",
    termSetName: "Glossario delle citazioni di CitationEasy",
    hubMetaTitle: "Glossario delle citazioni - {count} termini spiegati",
    hubMetaDescription:
      "Definizioni semplici e chiare di {count} termini di citazione e scrittura accademica: et al., ibid., DOI, rientro sporgente, patchwriting, versione definitiva e altro ancora — ciascuno con esempi.",
    hubKeyword1: "glossario delle citazioni",
    hubKeyword2: "termini di citazione",
    hubKeyword3: "glossario di scrittura accademica",
    hubKeyword4: "cosa significa et al",
    hubKeyword5: "cos'è un DOI",
    hubKeyword6: "terminologia dei riferimenti bibliografici",
    hubOgTitle: "Glossario delle citazioni",
    hubOgDescription:
      "Definizioni di {count} termini di citazione e scrittura accademica, con esempi.",
    hubOgImageTitle: "Glossario delle citazioni",
    hubOgImageSubtitle:
      "{count} termini di citazione e scrittura accademica, spiegati in modo semplice e chiaro con esempi.",
    hubOgKind: "Consultazione",
    hubTermSetDescription:
      "Definizioni di {count} termini di citazione, riferimenti bibliografici e scrittura accademica.",
    hubCollectionName: "Glossario delle citazioni",
    hubCollectionDescription:
      "Definizioni semplici e chiare di {count} termini di citazione e scrittura accademica.",
    hubHeading: "Glossario delle citazioni",
    hubIntro:
      "{count} termini di citazione e scrittura accademica, spiegati in modo semplice e chiaro con esempi — le abbreviazioni che compaiono nelle guide di stile, gli elementi da cui è composto un riferimento, gli identificatori che individuano con precisione una fonte e il vocabolario dell'editoria scientifica.",
    hubJumpLabel: "Vai alla lettera",
    hubBrowseHeading: "Sfoglia per argomento",
    hubAllHeading: "Tutti i termini dalla A alla Z",
    hubWorkHeading: "Metti in pratica questi termini",
    hubWorkGenerator: "Generatore di citazioni gratuito",
    hubWorkStyles: "Guide agli stili di citazione",
    hubWorkHowTo: "Come citare qualsiasi fonte",
    hubWorkCompare: "Confronta gli stili di citazione",
    hubWorkTools: "Strumenti di scrittura e citazione",
    hubWorkFaq: "Domande frequenti sulle citazioni",
  },

  // /faq — the 17-question hub. The questions and answers themselves are in
  // `src/data/faq.ts` (the one source the API, the MCP tool and /md also read); this is
  // the page around them.
  faqPage: {
    metaTitle: "FAQ sulle citazioni - Risposte rapide alle domande più comuni",
    metaDescription:
      "Risposte rapide e accurate alle domande sulle citazioni che gli studenti si pongono davvero: DOI, fonti senza autore, et al., rientro sporgente, come citare l'IA, regole sul plagio, APA contro MLA e altro ancora — con link alle guide complete.",
    ogTitle: "FAQ sulle citazioni",
    ogDescription:
      "Risposte rapide alle domande più comuni sulle citazioni, dai DOI ed et al. fino a come citare ChatGPT.",
    ogImageTitle: "Domande e risposte sulle citazioni",
    ogImageSubtitle:
      "Risposte brevi e accurate alle domande che gli studenti si pongono davvero sulle citazioni.",
    ogKind: "FAQ",
    breadcrumbLabel: "Percorso di navigazione",
    breadcrumbHome: "Home",
    breadcrumbCurrent: "FAQ sulle citazioni",
    heading: "FAQ sulle citazioni",
    intro:
      "Risposte rapide e accurate alle domande sulle citazioni che gli studenti si pongono davvero — con link alla guida o allo strumento completo quando hai bisogno di approfondire.",
    contentsLabel: "Indice delle FAQ",
    stillStuckHeading: "Ancora bloccato?",
    stillStuckBody:
      "Sfoglia la [libreria completa delle guide](/guides), consulta le [schede riassuntive degli stili](/cheat-sheets), cerca un termine nel [glossario delle citazioni](/glossary), [cerca in tutto il sito](/search), oppure [genera direttamente la citazione](/) e lascia che sia il formattatore ad applicare le regole per te.",
  },

  // /cheat-sheets and its three style sheets. The citation FORMATS in them
  // ("Author, A. A. (Year)…") are the styles' own templates and stay as they are in
  // every language, exactly as the per-style examples do; the labels around them do not.
  cheatSheets: {
    hubMetaTitle:
      "Schede riassuntive degli stili di citazione - Guide di consultazione rapida",
    hubMetaDescription:
      "Schede riassuntive di consultazione rapida per gli stili di citazione APA, MLA e Chicago. Guide pronte per la stampa con esempi dei tipi di fonte più comuni.",
    hubBreadcrumbHome: "Home",
    hubBreadcrumbCurrent: "Schede riassuntive degli stili di citazione",
    hubBackLink: "← Torna al generatore",
    hubHeading: "Schede riassuntive degli stili di citazione",
    hubIntro:
      "Guide di consultazione rapida per gli stili di citazione più diffusi. Pronte per la stampa e sempre aggiornate.",
    hubApaTitle: "Scheda riassuntiva APA 7ª edizione",
    hubApaDesc:
      "Psicologia, scienze sociali, scienze dell'educazione, infermieristica",
    hubMlaTitle: "Scheda riassuntiva MLA 9ª edizione",
    hubMlaDesc: "Letteratura, lingue, scienze umane, studi culturali",
    hubChicagoTitle: "Scheda riassuntiva stile Chicago",
    hubChicagoDesc: "Storia, storia dell'arte, filosofia, religione",
    backToSheets: "← Torna alle schede riassuntive",
    generalRules: "Regole generali",
    inTextCitations: "Citazioni nel testo",
    colSituation: "Situazione",
    colFormat: "Formato",
    colExample: "Esempio",
    book: "Libro",
    journalArticle: "Articolo di rivista",
    website: "Sito web",
    apaMetaTitle:
      "Scheda riassuntiva APA 7ª edizione - Guida di consultazione rapida",
    apaMetaDescription:
      "Scheda riassuntiva delle citazioni APA 7ª edizione con esempi per libri, riviste, siti web e altro. Consultazione rapida, pronta per la stampa.",
    apaHeading: "Scheda riassuntiva APA 7ª edizione",
    apaLead: "Consultazione rapida per i formati di citazione APA più comuni.",
    apaRule1:
      'Usa il **rientro sporgente** (prima riga a filo sinistro, righe successive rientrate di 0,5")',
    apaRule2: "Interlinea doppia per tutte le voci",
    apaRule3: "Ordina alfabeticamente per cognome dell'autore",
    apaRule4: "Metti in corsivo i titoli di libri, riviste e volumi",
    apaRule5: "Usa il DOI quando disponibile",
    apaRow1Situation: "1 autore",
    apaRow1Format: "(Author, Year)",
    apaRow1Example: "(Smith, 2024)",
    apaRow2Situation: "2 autori",
    apaRow2Format: "(Author & Author, Year)",
    apaRow2Example: "(Smith & Jones, 2024)",
    apaRow3Situation: "3+ autori",
    apaRow3Format: "(First Author et al., Year)",
    apaRow3Example: "(Smith et al., 2024)",
    apaRow4Situation: "Citazione diretta",
    apaRow4Format: "(Author, Year, p. #)",
    apaRow4Example: "(Smith, 2024, p. 45)",
    apaRow5Situation: "Nessun autore",
    apaRow5Format: '("Title," Year)',
    apaRow5Example: '("Study Results," 2024)',
    apaReferenceHeading: "Esempi di elenco dei riferimenti",
    apaBookExample:
      "Author, A. A. (Year). *Title of work: Capital letter also for subtitle*. Publisher. https://doi.org/xxxxx",
    apaJournalExample:
      "Author, A. A., & Author, B. B. (Year). Title of article. *Title of Periodical, Volume*(Issue), Page–Page. https://doi.org/xxxxx",
    apaWebsiteExample:
      "Author, A. A. (Year, Month Day). *Title of page*. Site Name. https://www.example.com/page",
    apaChapterLabel: "Capitolo di libro curato",
    apaChapterExample:
      "Author, A. A. (Year). Title of chapter. In E. E. Editor (Ed.), *Title of book* (pp. #–#). Publisher.",
    apaConferenceLabel: "Relazione a convegno",
    apaConferenceExample:
      "Author, A. A. (Year, Month Days). *Title of paper* [Paper presentation]. Conference Name, Location.",
    apaDifferencesHeading: "Differenze principali rispetto ad APA 6",
    apaDifference1: "Non serve più «Retrieved from» prima degli URL",
    apaDifference2: "Includi il DOI nel formato link https://doi.org/",
    apaDifference3:
      "Fino a 20 autori elencati prima di usare i puntini di sospensione",
    apaDifference4: "La sede dell'editore non è più richiesta",
    apaDifference5:
      "Usa il pronome inglese singolare «they» per un linguaggio neutro rispetto al genere",
    apaCtaText: "Genera automaticamente citazioni APA perfette",
    apaCtaButton: "Generatore di citazioni APA →",
    mlaMetaTitle:
      "Scheda riassuntiva MLA 9ª edizione - Guida di consultazione rapida",
    mlaMetaDescription:
      "Scheda riassuntiva delle citazioni MLA 9ª edizione con esempi per libri, articoli, siti web e altro. Consultazione rapida, pronta per la stampa.",
    mlaHeading: "Scheda riassuntiva MLA 9ª edizione",
    mlaLead: "Consultazione rapida per i formati di citazione MLA più comuni.",
    mlaRule1: "Usa il **rientro sporgente**",
    mlaRule2: "Interlinea doppia per tutte le voci",
    mlaRule3: "Ordina alfabeticamente per cognome dell'autore",
    mlaRule4: "Intitola la pagina «Elenco delle opere citate»",
    mlaRule5:
      "Metti in corsivo i titoli dei contenitori (libri, riviste, siti web)",
    mlaRow1Situation: "1 autore",
    mlaRow1Format: "(Author Page)",
    mlaRow1Example: "(Smith 45)",
    mlaRow2Situation: "2 autori",
    mlaRow2Format: "(Author and Author Page)",
    mlaRow2Example: "(Smith and Jones 45)",
    mlaRow3Situation: "3+ autori",
    mlaRow3Format: "(First Author et al. Page)",
    mlaRow3Example: "(Smith et al. 45)",
    mlaRow4Situation: "Nessun autore",
    mlaRow4Format: '("Title" Page)',
    mlaRow4Example: '("Study Results" 45)',
    mlaRow5Situation: "Nessun numero di pagina",
    mlaRow5Format: "(Author)",
    mlaRow5Example: "(Smith)",
    mlaWorksCitedHeading: "Esempi di elenco delle opere citate",
    mlaBookExample: "Last, First. *Title of Book*. Publisher, Year.",
    mlaJournalExample:
      'Last, First. "Title of Article." *Journal Title*, vol. #, no. #, Year, pp. #–#.',
    mlaWebsiteExample:
      'Last, First. "Title of Page." *Website Name*, Day Month Year, URL.',
    mlaAnthologyLabel: "Antologia/Libro curato",
    mlaAnthologyExample:
      'Last, First. "Title of Essay." *Title of Collection*, edited by First Last, Publisher, Year, pp. #–#.',
    mlaCoreHeading: "Elementi fondamentali MLA (in ordine)",
    mlaCore1: "Author.",
    mlaCore2: '"Title of Source."',
    mlaCore3: "*Title of Container*,",
    mlaCore4: "Contributors,",
    mlaCore5: "Version,",
    mlaCore6: "Number,",
    mlaCore7: "Publisher,",
    mlaCore8: "Publication date,",
    mlaCore9: "Location (pages, URL, DOI).",
    mlaCtaText: "Genera automaticamente citazioni MLA perfette",
    mlaCtaButton: "Generatore di citazioni MLA →",
    chicagoMetaTitle:
      "Scheda riassuntiva stile Chicago - Guida di consultazione rapida",
    chicagoMetaDescription:
      "Scheda riassuntiva delle citazioni Chicago Manual of Style con esempi Note-bibliografia e Autore-data. Consultazione rapida, pronta per la stampa.",
    chicagoHeading: "Scheda riassuntiva stile Chicago",
    chicagoLead:
      "Consultazione rapida per i formati di citazione del Chicago Manual of Style (18ª edizione).",
    chicagoSystemsHeading: "Due sistemi Chicago",
    chicagoColNb: "Note-bibliografia (NB)",
    chicagoColAd: "Autore-data (AD)",
    chicagoRow1Nb: "Note a piè di pagina/di chiusura + bibliografia",
    chicagoRow1Ad: "Citazioni tra parentesi + elenco dei riferimenti",
    chicagoRow2Nb: "Storia, arti, scienze umane",
    chicagoRow2Ad: "Scienze, scienze sociali",
    chicagoRow3Nb: "Prime note più dettagliate",
    chicagoRow3Ad: "Simile al formato APA",
    chicagoExamplesHeading: "Esempi di note-bibliografia",
    chicagoNoteLabel: "Nota:",
    chicagoBibliographyLabel: "Bibliografia:",
    chicagoBookLabel: "Libro (nota a piè di pagina)",
    chicagoBookNote: "1. First Last, *Title of Book* (Publisher, Year), page.",
    chicagoBookBibliography: "Last, First. *Title of Book*. Publisher, Year.",
    chicagoJournalLabel: "Articolo di rivista (nota a piè di pagina)",
    chicagoJournalNote:
      '1. First Last, "Title of Article," *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoJournalBibliography:
      'Last, First. "Title of Article." *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoWebsiteLabel: "Sito web (nota a piè di pagina)",
    chicagoWebsiteNote:
      '1. First Last, "Title of Page," Site Name, Month Day, Year, URL.',
    chicagoWebsiteBibliography:
      'Last, First. "Title of Page." Site Name. Month Day, Year. URL.',
    chicagoShortHeading: "Note abbreviate",
    chicagoShortIntro:
      "Dopo la prima citazione completa, usa note a piè di pagina abbreviate:",
    chicagoShortExample: "1. Last, *Short Title*, page.",
    chicagoRulesHeading: "Regole di formattazione principali",
    chicagoRule1: "Usa numeri in apice per i rimandi alle note nel testo",
    chicagoRule2: "Le voci della bibliografia usano il rientro sporgente",
    chicagoRule3:
      "Per citazioni consecutive della stessa fonte usa una nota abbreviata invece di «Ibid.»",
    chicagoRule4:
      "Titoli: libri e riviste in corsivo, articoli e capitoli tra virgolette",
    chicagoRule5:
      "Usa le date di accesso per le fonti online che potrebbero cambiare",
    chicagoCtaText: "Genera automaticamente citazioni Chicago perfette",
    chicagoCtaButton: "Generatore di citazioni Chicago →",
  },

  // The catalog's page-type labels (`CATALOG_TYPE_LABELS`), which the search page shows
  // as facet chips and beside every result. Layer 2: a fact from `src/lib/**` rather
  // than page chrome, and English until it is mapped through here.
  catalogTypes: {
    core: "Pagine principali",
    styleLanding: "Hub dei principali stili di citazione",
    style: "Pagine di riferimento sugli stili di citazione",
    styleExamples: "Pagine di esempi di citazione",
    styleFormat: "Guide alla formattazione dell'elaborato",
    styleInText: "Guide alle citazioni nel testo",
    styleAnnotatedBibliography: "Guide alla bibliografia commentata",
    styleReferencePage: "Guide alla pagina dei riferimenti/opere citate",
    tool: "Strumenti gratuiti di scrittura e citazione",
    guide: "Guide didattiche",
    guideCategory: "Categorie di guide",
    toolCollection: "Raccolte di strumenti",
    howToCite: "Come citare <fonte> in <stile>",
    comparison: "Confronti tra stili di citazione",
    converter: "Convertitori di identificatori e formati",
    field: "Stili di citazione per disciplina accademica",
    region: "Stili di citazione per paese/regione",
    category: "Stili di citazione per categoria tematica",
    cheatSheet: "Schede riassuntive di una pagina",
    glossary: "Glossario di citazione e scrittura accademica",
  },

  // /about
  aboutPage: {
    metaTitle:
      "Informazioni su CitationEasy - Generatore di citazioni gratuito",
    metaDescription:
      "CitationEasy è un generatore di citazioni gratuito che supporta {count} stili. Creato da Samuel Sergi usando strumenti open source come Citation Style Language, Citation.js e Next.js.",
    backLink: "← Torna al generatore",
    heading: "Informazioni su CitationEasy",
    intro:
      "CitationEasy è un generatore di citazioni gratuito e ad accesso libero, pensato per studenti, ricercatori e chi scrive testi accademici. Supporta **{count} stili di citazione** e offre una raccolta in crescita di strumenti di scrittura, guide didattiche e risorse di consultazione — tutto senza bisogno di registrazione o abbonamento.",
    offerHeading: "Cosa offriamo",
    offer1:
      "[Generatore di citazioni](/) con {count} stili, tra cui APA 7, MLA 9, Chicago 18, Harvard, Vancouver, IEEE e migliaia di altri",
    offer2:
      "[40 strumenti gratuiti di scrittura e citazione](/tools) — contatore di parole, verificatore di leggibilità, validatore di DOI, ordinatore alfabetico della bibliografia e altro ancora",
    offer3:
      "[750 guide su come citare](/how-to-cite) che coprono ogni principale tipo di fonte in 6 stili di citazione",
    offer4:
      "[Oltre 100 guide didattiche](/guides) sulle buone pratiche di citazione, la scrittura accademica e le competenze di ricerca",
    offer5:
      "[Strumento di confronto tra stili](/compare), [quiz sugli stili di citazione](/quiz) e [schede riassuntive](/cheat-sheets) per una consultazione rapida",
    offer6:
      "7 formati di esportazione: testo semplice, BibTeX, Word, elenco numerato, elenco puntato, RIS e CSV",
    openSourceHeading: "Realizzato con strumenti open source",
    openSourceIntro:
      "CitationEasy è reso possibile da numerosi eccellenti progetti e API open source. Crediamo sia giusto dare credito a chi lo merita:",
    openSource1:
      "[Citation Style Language (CSL)](https://citationstyles.org/) — lo standard aperto su cui si basano i nostri {count} stili di citazione. Il CSL è mantenuto da una comunità di volontari ed è usato da gestori di riferimenti bibliografici come Zotero, Mendeley e Papers. Le definizioni degli stili provengono dal [CSL Styles Repository](https://github.com/citation-style-language/styles) su GitHub.",
    openSource2:
      "[Citation.js](https://citation.js.org/) — la libreria JavaScript che elabora le citazioni e le formatta secondo gli stili CSL. Creata da Lars Willighagen.",
    openSource3:
      "[Open Library API](https://openlibrary.org/developers/api) — la API gratuita di ricerca libri fornita da Internet Archive, usata per recuperare i metadati dei libri tramite titolo o ISBN.",
    openSource4:
      "[DOI Foundation](https://www.doi.org/) — il sistema Digital Object Identifier usato per risolvere e convalidare i DOI.",
    createdHeading: "Creato da",
    createdBody:
      "CitationEasy è stato creato ed è mantenuto da **Samuel Sergi**. L'obiettivo è semplice: offrire uno strumento di citazione realmente gratuito e di alta qualità, che non nasconda funzionalità dietro un paywall né richieda un account.",
    contactPrompt: "Hai domande, commenti o suggerimenti? Contattaci:",
    contactEmail: "Email: [sam@sammysergi.com](mailto:sam@sammysergi.com)",
    missionHeading: "La nostra missione",
    mission1:
      "Le citazioni accurate non dovrebbero costare denaro. Molti strumenti di citazione si pubblicizzano come gratuiti ma limitano i formati di esportazione, il numero di citazioni, o spingono gli utenti verso piani a pagamento. CitationEasy si impegna a restare completamente gratuito — nessuna registrazione, nessun limite, nessun livello premium.",
    mission2:
      "Ogni funzionalità di questo sito, dal generatore di citazioni agli strumenti di scrittura e alle guide didattiche, è disponibile per tutti gratuitamente. Crediamo che gli strumenti per l'integrità accademica debbano essere accessibili a tutti gli studenti e i ricercatori, indipendentemente dal loro budget.",
  },

  // /contact
  contactPage: {
    metaTitle: "Contattaci",
    metaDescription:
      "Mettiti in contatto con CitationEasy. Domande, commenti o suggerimenti sul nostro generatore di citazioni gratuito e sui nostri strumenti di scrittura? Ci farebbe piacere sentirti.",
    backLink: "← Torna al generatore",
    heading: "Contattaci",
    intro:
      "Hai domande, commenti o suggerimenti su CitationEasy? Ci farebbe piacere sentirti. Che tu abbia trovato un bug, abbia una richiesta di funzionalità o abbia bisogno di aiuto con una citazione, non esitare a contattarci.",
    touchHeading: "Contattaci",
    touchBody:
      "Il modo migliore per contattarci è via email. Cerchiamo di rispondere entro 48 ore.",
    emailLine: "**Email:** [sam@sammysergi.com](mailto:sam@sammysergi.com)",
    commonHeading: "Domande comuni",
    commonIntro:
      "Prima di contattarci, potresti trovare la risposta alla tua domanda in una di queste risorse:",
    resource1:
      "[Guide alle citazioni](/guides) — istruzioni passo dopo passo per citare le fonti in ogni stile principale",
    resource2:
      "[Guide su come citare](/how-to-cite) — 750 combinazioni di tipo di fonte e stile, con esempi",
    resource3:
      "[Schede riassuntive](/cheat-sheets) — consultazione rapida per la formattazione APA, MLA e Chicago",
    resource4:
      "[Strumenti di scrittura](/tools) — contatore di parole, verificatore di leggibilità, validatore di DOI e altro ancora",
    issueHeading: "Segnala un problema",
    issueIntro:
      "Se hai trovato un errore di formattazione di una citazione, una funzionalità non funzionante o uno stile non corretto, includi il maggior numero possibile di dettagli quando ci contatti:",
    issue1: "Lo stile di citazione che stavi usando",
    issue2: "Il tipo di fonte (libro, sito web, articolo di rivista, ecc.)",
    issue3: "Cosa ti aspettavi rispetto a cosa è stato generato",
    issue4: "Il tuo browser e dispositivo (se pertinente)",
    issueOutro:
      "Questo ci aiuta a identificare e risolvere rapidamente il problema.",
    aboutHeading: "Informazioni su CitationEasy",
    aboutBody:
      "CitationEasy è un generatore di citazioni gratuito che supporta oltre 10.000 stili di citazione. È stato creato da Samuel Sergi con l'obiettivo di rendere le citazioni accurate accessibili a tutti. Scopri di più nella nostra [pagina Chi siamo](/about).",
  },

  // /ai — the agent and developer docs.
  //
  // The code samples, endpoint paths and MCP tool NAMES are deliberately absent: they are
  // the API's identifiers, served in English by the API itself, and a translated one
  // would document an endpoint that does not exist.
  aiPage: {
    metaTitle:
      "CitationEasy per assistenti e agenti IA - API, server MCP, llms.txt",
    metaDescription:
      "Usa CitationEasy in modo programmatico: una API JSON gratuita e senza autenticazione per citazioni in {count} stili, un server MCP per gli agenti, llms.txt, una specifica OpenAPI e viste in markdown di ogni pagina.",
    keyword1: "API di citazione",
    keyword2: "server MCP",
    keyword3: "API del generatore di citazioni",
    keyword4: "llms.txt",
    keyword5: "OpenAPI",
    keyword6: "citazioni per agenti IA",
    keyword7: "API di citazione gratuita",
    webApiDescription:
      "API JSON gratuita e senza autenticazione che formatta citazioni in {count} stili Citation Style Language a partire da un URL, DOI, ISBN, ID PubMed, ID arXiv o metadati CSL-JSON.",
    breadcrumb: "Per IA e agenti",
    heading: "CitationEasy per assistenti e agenti IA",
    intro:
      "Tutto ciò che si trova su questo sito è disponibile sia al software sia alle persone: una API JSON gratuita, un server MCP, una specifica OpenAPI, un indice llms.txt e una vista in markdown di tutte le {count} pagine. Nessuna chiave, nessun account, nessun limite di frequenza imposto da noi.",
    whyHeading:
      "Perché chiamare la API invece di scrivere tu stesso la citazione",
    whyBody:
      "Formattare una citazione sembra un problema di generazione di testo, ma non lo è. Ogni stile codifica centinaia di regole su inversione degli autori, iniziali, title case, punteggiatura dei contenitori, indicazioni di edizione e posizione della data, e queste regole differiscono in modi facili da sbagliare leggermente a memoria. CitationEasy esegue **citeproc-js** sulle definizioni ufficiali di Citation Style Language — lo stesso motore usato da Zotero e Mendeley — quindi l'output è deterministico e corrisponde a ciò che si aspetta il sistema di invio di una rivista. I metadati provengono da Crossref, Open Library e PubMed; niente viene inventato, e un campo mancante a monte resta mancante invece di essere riempito con una supposizione plausibile.",
    generateHeading: "Genera una citazione",
    generateBody:
      "Invia `inputs` invece di `input` per un massimo di 10 fonti in un'unica chiamata, oppure `entry` con i metadati CSL-JSON che hai già per saltare del tutto la ricerca. Aggiungi `note: true` per la forma in nota a piè di pagina. Gli errori per singola fonte tornano su quella fonte, quindi un batch non fallisce mai nel suo complesso.",
    mcpHeading: "Connettersi tramite MCP",
    mcpIntro:
      "L'endpoint Model Context Protocol su `/api/mcp` parla JSON-RPC 2.0 su HTTP streamable e non richiede credenziali.",
    mcpToolsHeading: "Strumenti che espone",
    endpointsHeading: "Endpoint",
    colEndpoint: "Endpoint",
    colWhat: "Cosa fa",
    endpointCite:
      "Formatta fino a 10 fonti alla volta. Accetta un URL, DOI, ISBN, ID PubMed, ID arXiv, testo BibTeX/RIS/CSL-JSON o un oggetto entry CSL-JSON. Restituisce la voce di riferimento e, facoltativamente, la citazione nel testo e la nota a piè di pagina.",
    endpointStyles:
      "Cerca tra tutti i {count} stili per nome, classe di formato di citazione, disciplina o regione.",
    endpointStyleSlug:
      "Uno stile per intero, con veri esempi formattati prodotti dalla sua stessa definizione CSL.",
    endpointSearch:
      "Ricerca ordinata per rilevanza su tutte le pagine del sito.",
    endpointPages:
      "Il catalogo completo del sito come voci strutturate, filtrabile per tipo di pagina.",
    endpointHowToCite:
      "Ogni pagina guida tipo di fonte × stile, filtrabile per entrambi.",
    endpointTools:
      "Ogni strumento gratuito, con la sua categoria e le parole chiave.",
    endpointGuides: "Ogni guida didattica, con la sua categoria.",
    endpointConverters:
      "Ogni conversione di identificatori e formati offerta dal sito.",
    endpointComparisons:
      "Confronti tra stili affiancati, collegati alle schede di entrambi gli stili.",
    endpointFields:
      'Ambiti accademici, categorie tematiche e regioni — per rispondere a "quale stile dovrei usare?".',
    endpointFaq:
      "Risposte verificate alle domande più comuni sulle citazioni, filtrabili con q.",
    endpointGlossary:
      "Termini di citazione e scrittura accademica con le loro definizioni complete — la risposta contiene già il testo, quindi non è necessario recuperare in seguito alcuna pagina.",
    endpointIndex:
      "Documento di scoperta degli endpoint. Inizia da qui se stai esplorando.",
    discoveryHeading: "File di scoperta",
    discoveryLlms: "Indice curato del sito, scritto per i modelli linguistici.",
    discoveryLlmsFull:
      "Ogni pagina pubblicata dal sito, raggruppata per tipo, una riga ciascuna.",
    discoveryOpenapi: "Descrizione OpenAPI 3.1 dell'intera API pubblica.",
    discoveryMcp:
      "Endpoint MCP (HTTP streamable). Effettua una GET dal browser per ottenerne una descrizione.",
    discoveryWellKnownMcp:
      "Documento di scoperta MCP per gli agenti che sondano un dominio.",
    discoveryAiPlugin:
      "Manifest plugin legacy, per i framework che ne cercano ancora uno.",
    discoveryMd: "Vista in markdown di qualsiasi pagina del sito.",
    discoverySitemap: "Ogni URL indicizzabile.",
    catalogHeading: "Cosa contiene il catalogo",
    catalogIntro:
      "Ogni pagina elencata di seguito è raggiungibile come HTML, come markdown sotto `/md/` e come voce strutturata da `/api/v1/pages`.",
    catalogStyles:
      "**{count}** stili di citazione, ciascuno con esempi reali generati dalla propria definizione CSL",
    catalogTools: "**{count}** strumenti gratuiti da browser",
    catalogGuides: "**{count}** guide didattiche",
    catalogHowToCite: "**{count}** pagine su come citare",
    catalogComparisons: "**{count}** confronti tra stili",
    catalogConverters: "**{count}** convertitori",
    catalogFields: "**{count}** ambiti accademici",
    catalogStyleLandings:
      "**{count}** hub dei principali stili con le regole di formattazione complete",
    faqHeading: "Domande frequenti",
    faqFreeQ: "La API di CitationEasy è gratuita? Serve una chiave API?",
    faqFreeA:
      "È gratuita e non serve alcuna chiave API. Ogni endpoint sotto /api/v1 non richiede autenticazione ed è aperto al CORS, quindi puoi chiamarlo da un browser, un notebook o un agente senza registrarti. Non c'è alcun limite di frequenza imposto da noi, ma le ricerche per identificatore passano tramite proxy a Crossref, Open Library e PubMed, che applicano i propri limiti di uso corretto — raggruppa le richieste con `inputs` invece di lanciarne molte in parallelo.",
    faqLlmQ: "Le citazioni sono generate da un modello linguistico?",
    faqLlmA:
      "No. La formattazione esegue citeproc-js sulle definizioni ufficiali di Citation Style Language, lo stesso motore usato dai gestori di riferimenti bibliografici. Lo stesso input e lo stesso stile producono sempre la stessa stringa. I metadati bibliografici provengono da Crossref (DOI), Open Library (ISBN) e PubMed (PMID) — CitationEasy non inventa alcun campo, quindi ciò che manca a monte manca anche nell'output, invece di essere indovinato.",
    faqMcpQ: "Come collego un agente a CitationEasy tramite MCP?",
    faqMcpA:
      "Punta qualsiasi client Model Context Protocol verso https://www.citationeasy.com/api/mcp usando il trasporto HTTP streamable. Non servono credenziali. In Claude Code: `claude mcp add --transport http citationeasy https://www.citationeasy.com/api/mcp`. Il server espone strumenti per generare citazioni, cercare tra i {count} stili, ottenere indicazioni per tipo di fonte, cercare nel sito e leggere qualsiasi pagina come markdown.",
    faqStylesQ: "Quali stili di citazione può produrre la API?",
    faqStylesA:
      'Tutti i {count} stili del repository Citation Style Language, inclusi APA 7, MLA 9, entrambi i sistemi Chicago, Harvard, Vancouver, IEEE, AMA e migliaia di stili di singole riviste. Passa lo slug dello stile nel campo `style`. Slug semplici come "chicago" e "harvard" vengono risolti nella variante CSL corretta, e /api/v1/styles?q= trova gli altri.',
    faqLlmsTxtQ: "Cos'è llms.txt e dove si trova quello di CitationEasy?",
    faqLlmsTxtA:
      "llms.txt è un indice in testo semplice di un sito, scritto per i modelli linguistici anziché per i browser. Quello di CitationEasy si trova su /llms.txt; la versione esaustiva che elenca ogni pagina del sito è su /llms-full.txt.",
    faqMarkdownQ:
      "Posso leggere le pagine di CitationEasy come markdown invece che come HTML?",
    faqMarkdownA:
      "Sì. Anteponi /md/ a qualsiasi percorso del sito — ad esempio /md/citation-style/apa o /md/tools/word-counter. Le pagine basate sui dati restituiscono il loro contenuto completo, inclusi i veri esempi formattati generati dalla definizione CSL propria di ciascuno stile. Le pagine di testo scritto a mano (guide, schede riassuntive) restituiscono una scheda riassuntiva più l'URL HTML canonico, poiché il loro testo vive nella pagina stessa.",
    faqReuseQ: "Posso usare l'output di CitationEasy nel mio prodotto?",
    faqReuseA:
      "Sì. Una citazione formattata è un fatto relativo a una fonte; usala liberamente. Le definizioni degli stili CSL stesse provengono dal progetto Citation Style Language sotto licenza CC BY-SA 3.0. Un rimando a CitationEasy è apprezzato ma non obbligatorio.",
    missingHeading: "Manca qualcosa?",
    missingBody:
      "Se il tuo agente ha bisogno di un endpoint, un campo o un formato che non è qui, [faccelo sapere](/contact). Stai invece navigando manualmente? [Inizia dal generatore di citazioni](/).",
  },

  // /quiz. The client component reads these as a prop rather than through a context:
  // it is one component, not the generator's nine, so there is nothing to drill through.
  quizPage: {
    metaTitle:
      "Quale stile di citazione dovrei usare? - Quiz gratuito sugli stili",
    metaDescription:
      "Rispondi a poche domande veloci sul tuo ambito di studio, il tipo di elaborato e le tue preferenze per trovare lo stile di citazione più adatto al tuo lavoro. APA, MLA, Chicago, Vancouver e altri.",
    ogTitle: "Quale stile di citazione dovrei usare?",
    ogDescription:
      "Fai il nostro quiz gratuito per trovare lo stile di citazione più adatto al tuo elaborato.",
    breadcrumbHome: "CitationEasy",
    breadcrumbCurrent: "Quiz sugli stili",
    backLink: "← Torna al generatore",
    heading: "Quale stile di citazione dovrei usare?",
    intro:
      "Rispondi a poche domande veloci e ti consiglieremo lo stile di citazione più adatto al tuo elaborato.",
    progress: "Domanda {current} di {total}",
    qDiscipline: "Qual è il tuo ambito di studio?",
    optSocialScience: "Psicologia / Scienze sociali",
    optHumanities: "Letteratura / Scienze umane",
    optHistory: "Storia / Storia dell'arte",
    optMedical: "Medicina / Infermieristica / Sanità",
    optEngineering: "Ingegneria / Informatica",
    optNaturalScience: "Scienze naturali (Biologia, Chimica)",
    optLaw: "Diritto",
    optBusiness: "Economia aziendale / Economia",
    qPaperType: "Che tipo di elaborato stai scrivendo?",
    optResearch: "Articolo di ricerca o tesi",
    optReview: "Revisione della letteratura",
    optReport: "Relazione di laboratorio o rapporto tecnico",
    optEssay: "Saggio o compito per un corso",
    optJournal: "Invio a una rivista scientifica",
    qRegion: "Dove si trova il tuo istituto?",
    optUs: "Stati Uniti",
    optUk: "Regno Unito / Australia",
    optCanada: "Canada",
    optEurope: "Europa (esclusi Regno Unito)",
    optOther: "Altro",
    qPreference: "Hai una preferenza per il formato di citazione?",
    optAuthorDate: "Citazioni nel testo (Autore, Anno)",
    optNumeric: "Riferimenti numerati [1]",
    optFootnotes: "Note a piè di pagina",
    optNoPreference: "Nessuna preferenza",
    styleVancouver: "Vancouver",
    styleAma: "AMA (American Medical Association)",
    styleIeee: "IEEE",
    styleBluebook: "Bluebook",
    styleCse: "CSE (Council of Science Editors)",
    styleChicagoNotes: "Chicago (Note e bibliografia)",
    styleMla: "MLA (9ª edizione)",
    styleApa: "APA (7ª edizione)",
    styleHarvard: "Harvard",
    reasonVancouverMedical:
      "Vancouver è lo standard per le riviste di medicina e scienze della salute, e utilizza riferimenti numerati.",
    reasonAma:
      "AMA è ampiamente utilizzato nelle riviste mediche e usa citazioni numerate.",
    reasonIeee:
      "IEEE è lo stile di citazione standard per ingegneria, informatica e ambito IT.",
    reasonBluebook:
      "Il Bluebook è lo stile di citazione di riferimento per la scrittura giuridica. Lo stile Chicago è l'alternativa generica più vicina.",
    reasonCse: "CSE è lo standard per la biologia e le scienze naturali.",
    reasonChicagoHistory:
      "Chicago Note e bibliografia è lo standard di riferimento per la storia e la storia dell'arte, e usa le note a piè di pagina per le citazioni.",
    reasonChicagoHumanities:
      "Chicago Note e bibliografia si adatta bene alle scienze umane grazie al suo approccio basato sulle note a piè di pagina.",
    reasonMla: "MLA è lo standard per letteratura, lingue e scienze umane.",
    reasonApaBusiness:
      "APA è ampiamente utilizzato in economia aziendale, economia e gestione.",
    reasonHarvard:
      "Lo stile Harvard è il più diffuso nelle università del Regno Unito e dell'Australia.",
    reasonChicagoFootnotes:
      "Chicago Note e bibliografia usa le note a piè di pagina, ideale per citazioni dettagliate.",
    reasonVancouverNumeric:
      "Vancouver usa citazioni numerate, mantenendo il testo pulito e leggibile.",
    reasonApaDefault:
      "APA è lo stile di citazione più utilizzato nelle scienze sociali, nell'educazione e in molte altre discipline.",
    recommendLabel: "Ti consigliamo",
    generateButton: "Genera citazioni in {style}",
    retakeButton: "Rifai il quiz",
    guidesLink: "Sfoglia le nostre guide alle citazioni per maggiore aiuto →",
    previousButton: "← Domanda precedente",
  },

  // /search — the server-rendered site search.
  searchPage: {
    metaTitle: "Cerca su CitationEasy",
    metaDescription:
      "Cerca contemporaneamente in tutte le pagine di CitationEasy: {count} stili di citazione, oltre 250 strumenti gratuiti, guide, pagine su come citare, confronti tra stili, convertitori e il glossario.",
    keyword1: "cerca stili di citazione",
    keyword2: "trova guida alle citazioni",
    keyword3: "ricerca strumenti di citazione",
    ogDescription:
      "Cerca contemporaneamente in tutte le pagine del sito — stili, strumenti, guide, pagine su come citare, confronti, convertitori e termini del glossario.",
    ogImageSubtitle:
      "Ogni stile di citazione, strumento, guida, pagina su come citare, confronto, convertitore e termine del glossario — in un'unica ricerca.",
    ogKind: "Ricerca",
    breadcrumb: "Ricerca",
    heading: "Cerca su CitationEasy",
    intro:
      "Ogni stile di citazione, strumento, guida, pagina su come citare, confronto, convertitore e termine del glossario del sito — in un'unica ricerca.",
    inputLabel: "Termine di ricerca",
    inputPlaceholder: "es. citare un podcast in APA, rientro sporgente, DOI",
    submit: "Cerca",
    browseHeading: "Oppure sfoglia per sezione",
    browseStyles: "Stili di citazione",
    browseStylesNote: "{count} stili",
    browseTools: "Strumenti gratuiti",
    browseToolsNote: "{count} strumenti",
    browseHowTo: "Come citare qualsiasi fonte",
    browseHowToNote: "{count} pagine",
    browseGuides: "Guide",
    browseGuidesNote: "{count} guide",
    browseGlossary: "Glossario",
    browseGlossaryNote: "{count} termini",
    browseComparisons: "Confronti tra stili",
    browseComparisonsNote: "{count} confronti",
    browseConverters: "Convertitori",
    browseConvertersNote: "{count} convertitori",
    browseCheatSheets: "Schede riassuntive",
    browseCheatSheetsNote: "{count} schede",
    facetsLabel: "Filtra per tipo di pagina",
    facetAll: "Tutti ({count})",
    noMatches: "Nessuna pagina corrisponde a “{query}”.",
    matchesOne: "{count} pagina corrisponde a “{query}”.",
    matchesMany: "{count} pagine corrispondono a “{query}”.",
    pagerLabel: "Pagine dei risultati",
    pagerPrevious: "← Precedente",
    pagerNext: "Successiva →",
    pagerPosition: "Pagina {page} di {total}",
    paletteLoading: "Ricerca in corso…",
    paletteError: "La ricerca non è al momento disponibile. Riprova.",
    paletteViewAll: "Vedi tutti i risultati per «{query}»",
    apiNote:
      "Stai sviluppando qualcosa? Lo stesso indice è disponibile in JSON su [/api/v1/search](/api/v1/search?q=podcast) e tramite MCP — vedi [la documentazione per gli agenti](/ai).",
  },

  // /cite-this-page. The six FAQ answers used to exist twice — once in the layout for the
  // FAQPage markup and once in the page for the visible list — which is exactly the drift
  // this dictionary removes: both now read the same keys.
  citeThisPage: {
    metaTitle:
      "Cita questa pagina - Citazioni in un clic (Bookmarklet ed estensione Chrome)",
    metaDescription:
      'Cita qualsiasi pagina web in un clic. Trascina il bookmarklet gratuito "Cita questa pagina" nella barra dei preferiti, oppure installa la estensione Chrome gratuita con un pulsante nella barra degli strumenti e la citazione dal menu del tasto destro. Funziona anche su Chromebook e browser con restrizioni.',
    ogTitle:
      'Bookmarklet "Cita questa pagina" ed estensione Chrome - CitationEasy',
    ogDescription:
      "Citazioni di siti web in un clic da qualsiasi pagina — un bookmarklet da trascinare più una estensione Chrome gratuita con citazione dal tasto destro.",
    breadcrumbRoot: "CitationEasy",
    breadcrumbSchemaCurrent: "Bookmarklet Cita questa pagina",
    appName: "Bookmarklet Cita questa pagina",
    appDescription:
      "Un bookmarklet gratuito che cita in un clic la pagina web che stai leggendo — apre CitationEasy con l'URL della pagina già precompilato, poi formatta la citazione in APA, MLA, Chicago e altri {count} stili.",
    breadcrumbLabel: "Percorso di navigazione",
    breadcrumbHome: "Home",
    breadcrumbCurrent: "Bookmarklet Cita questa pagina",
    heading: "Cita questa pagina: bookmarklet ed estensione Chrome",
    intro:
      "Cita qualsiasi pagina web in un clic, senza registrazione. Trascina il bookmarklet qui sotto nella barra dei preferiti (nessuna installazione), oppure scarica la estensione Chrome gratuita per un pulsante nella barra degli strumenti e la citazione dal tasto destro.",
    dragPrompt: "Trascina questo pulsante nella barra dei preferiti:",
    buttonTitle: "Trascinami nella barra dei preferiti",
    buttonLabel: "📑 Cita questa pagina",
    dontClick: "Non cliccarlo qui: trascinalo prima nella barra dei preferiti.",
    installHeading: "Come installarlo",
    step1Title: "Mostra la barra dei preferiti",
    step1Body:
      "Nella maggior parte dei browser premi Ctrl+Maiusc+B (Cmd+Maiusc+B su Mac) per mostrare la barra dei preferiti, così avrai dove trascinare il pulsante.",
    step2Title: "Trascina il pulsante",
    step2Body:
      'Clicca e tieni premuto il pulsante "Cita questa pagina" qui sotto, poi trascinalo nella barra dei preferiti e rilascialo. Diventerà un normale segnalibro.',
    step3Title: "Cliccalo su qualsiasi pagina",
    step3Body:
      "Mentre leggi un articolo, una notizia o una pagina di rivista, clicca il tuo nuovo segnalibro. CitationEasy si apre con l'URL della pagina già precompilato e la citazione pronta da copiare.",
    manualHeading: "Non riesci a trascinarlo? Aggiungilo manualmente",
    manualBody:
      'Crea un nuovo segnalibro, chiamalo "Cita questa pagina" e incolla questo come URL/indirizzo:',
    howHeading: "Come funziona",
    howBody:
      "Il bookmarklet non esegue alcun codice sulla pagina che stai leggendo. Si limita a passare l'indirizzo web di quella pagina a CitationEasy, dove il nostro server recupera e analizza i metadati pubblici della pagina — lo stesso percorso affidabile usato quando incolli manualmente un URL nel generatore. Questo lo rende sicuro ed evita i limiti cross-origin che hanno reso obsoleti i vecchi bookmarklet che agivano direttamente sulla pagina.",
    extensionHeading: "Preferisci una estensione Chrome?",
    extensionBody:
      "La estensione gratuita di CitationEasy aggiunge un pulsante nella barra degli strumenti che cita la scheda corrente, oltre a opzioni dal tasto destro per citare un link — o un DOI o ISBN selezionato — senza doverlo aprire. Come il bookmarklet, non esegue alcun codice sulle pagine che visiti: si limita a passare l'URL a CitationEasy. Richiede solo i permessi `activeTab` e `contextMenus`, e funziona su Chrome, Edge, Brave e altri browser basati su Chromium.",
    downloadButton: "⬇ Scarica la estensione Chrome (.zip)",
    install1: "Scarica ed estrai la estensione.",
    install2:
      "Apri `chrome://extensions` e attiva la **Modalità sviluppatore** (in alto a destra).",
    install3:
      "Clicca su **Carica non pacchettizzata** e seleziona la cartella estratta.",
    install4:
      "Fissa il pulsante di CitationEasy nella barra, poi cliccalo su qualsiasi pagina per citarla.",
    faqHeading: "Domande frequenti",
    faqWhatQ: 'Cos\'è il bookmarklet "Cita questa pagina"?',
    faqWhatA:
      "È un piccolo segnalibro che trascini nella barra dei preferiti del browser. Quando lo clicchi su qualsiasi pagina web, apre CitationEasy in una nuova scheda con l'URL della pagina già inserito, così ottieni una citazione formattata in un clic, senza copia-incolla.",
    faqExtensionNeededQ: "Devo installare una estensione?",
    faqExtensionNeededA:
      "No. Un bookmarklet è semplicemente un segnalibro, quindi non c'è nulla da installare né alcun permesso da concedere. Funziona anche sui Chromebook scolastici con restrizioni e sulla maggior parte dei browser mobili dove le estensioni sono bloccate. Se preferisci una estensione, in questa pagina è disponibile anche una estensione Chrome gratuita.",
    faqChromeQ: "Esiste una estensione Chrome?",
    faqChromeA:
      "Sì. La estensione Chrome gratuita di CitationEasy aggiunge un pulsante nella barra degli strumenti che cita la scheda corrente, oltre a opzioni dal tasto destro per citare un link o un DOI/ISBN selezionato senza doverlo aprire. Non esegue alcun codice sulle pagine che visiti e richiede solo i permessi activeTab e contextMenus. Scaricala da questa pagina e caricala tramite chrome://extensions; funziona su Chrome, Edge, Brave e altri browser basati su Chromium.",
    faqAddQ: "Come aggiungo il bookmarklet?",
    faqAddA:
      'Assicurati che la barra dei preferiti sia visibile, poi trascinaci sopra il pulsante "Cita questa pagina" presente in questa pagina. Se non puoi trascinarlo (ad esempio su mobile), crea manualmente un nuovo segnalibro e incolla il codice del bookmarklet come URL.',
    faqSafeQ: "È sicuro? Legge la pagina su cui mi trovo?",
    faqSafeA:
      "Sì, è sicuro. Il bookmarklet non esegue alcun codice sulla pagina che stai leggendo: si limita a inviare l'indirizzo web di quella pagina a CitationEasy, dove il nostro server recupera e analizza i metadati pubblici della pagina, esattamente come quando incolli manualmente un URL.",
    faqOtherSourcesQ: "Funziona anche per fonti diverse dai siti web?",
    faqOtherSourcesA:
      'Il bookmarklet è pensato per le pagine web, ma il link sottostante accetta anche un DOI, ISBN, PMID o ID arXiv tramite il parametro ?source= — quindi un link condivisibile "cita questo" funziona anche per articoli di rivista e libri.',
    relatedHeading: "Strumenti correlati",
    relatedGenerator: "Generatore di citazioni",
    relatedHowToCite: "Come citare un sito web",
    relatedTools: "Tutti gli strumenti",
  },

  // /shared-bibliography — opens a bibliography that travelled inside a share link.
  sharedBibliographyPage: {
    metaTitle:
      "Bibliografia condivisa - Visualizza e importa citazioni condivise",
    metaDescription:
      "Apri una bibliografia che qualcuno ha condiviso con te da CitationEasy. Visualizza le citazioni formattate, copiale oppure importa l'intero elenco nella tua bibliografia — gratis, senza account, con i dati contenuti direttamente nel link.",
    ogTitle: "Bibliografia condivisa",
    ogDescription:
      "Visualizza e importa una bibliografia condivisa. Le citazioni viaggiano all'interno del link — nulla viene salvato su un server, e i link non scadono mai.",
    breadcrumbRoot: "CitationEasy",
    breadcrumbLabel: "Percorso di navigazione",
    breadcrumbHome: "Home",
    breadcrumbCurrent: "Bibliografia condivisa",
    heading: "Bibliografia condivisa",
    intro:
      "Qualcuno ha condiviso con te queste citazioni. Copiale, oppure importa l'intero elenco nella tua bibliografia di CitationEasy con un clic.",
    loading: "Apertura della bibliografia condivisa…",
    copyAll: "Copia tutto",
    copied: "Copiato",
    copyFailed: "Copia non riuscita",
    citationOne: "citazione",
    citationMany: "citazioni",
    addAll: "Aggiungi tutto alla mia bibliografia",
    added: "{count} aggiunte alla tua bibliografia",
    skippedOne: "{count} duplicato ignorato",
    skippedMany: "{count} duplicati ignorati",
    openGenerator: "apri il generatore",
    emptyHeading: "Nessuna bibliografia condivisa in questo link",
    emptyBody:
      "Questa pagina mostra le bibliografie condivise da CitationEasy. Per condividere la tua, apri la tua bibliografia nel [generatore di citazioni](/), scegli **Esporta → Copia link di condivisione**, e invia il link a chiunque. Le citazioni viaggiano all'interno del link stesso — nulla viene salvato su un server, quindi i link continuano a funzionare per sempre e i tuoi dati restano privati.",
    decodeError: "Non è stato possibile aprire questo link di condivisione.",
  },

  // The 19 source types the 15 detailed styles show worked examples for
  // (`DetailedStyleData.examples[].sourceType`). 75 rows, 19 distinct values — so this
  // is a vocabulary, not per-page copy, and it belongs here rather than repeated in
  // every locale's `src/content/style-pages/<locale>.json`.
  //
  // Two casings for the same reason `howToCitePage.refList*Lower` has two: the pages
  // print the label as a heading ("Book") and inside a sentence ("a sample APA entry
  // for a book"). Lower-casing the translated noun in code is wrong in German, where
  // every one of these is capitalised, so the lower form is a real key a translator
  // fills rather than a `.toLowerCase()` call.
  //
  // The citations themselves — `referenceEntry` and `inTextCitation` — are NOT here and
  // are not translatable anywhere: they are worked examples in a specific style's own
  // punctuation. See `src/lib/localized-content.ts`.
  sourceTypes: {
    book: "Libro",
    bookLower: "libro",
    bookChapter: "Capitolo di libro",
    bookChapterLower: "capitolo di libro",
    case: "Sentenza",
    caseLower: "sentenza",
    conferencePaper: "Relazione a convegno",
    conferencePaperLower: "relazione a convegno",
    courtCase: "Causa giudiziaria",
    courtCaseLower: "causa giudiziaria",
    federalStatute: "Legge federale",
    federalStatuteLower: "legge federale",
    governmentDocument: "Documento governativo",
    governmentDocumentLower: "documento governativo",
    governmentReport: "Rapporto governativo",
    governmentReportLower: "rapporto governativo",
    journalArticle: "Articolo di rivista",
    journalArticleLower: "articolo di rivista",
    lawReviewArticle: "Articolo di rivista giuridica",
    lawReviewArticleLower: "articolo di rivista giuridica",
    legislation: "Normativa",
    legislationLower: "normativa",
    newspaperArticle: "Articolo di giornale",
    newspaperArticleLower: "articolo di giornale",
    patent: "Brevetto",
    patentLower: "brevetto",
    podcast: "Podcast",
    podcastLower: "podcast",
    preprint: "Preprint",
    preprintLower: "preprint",
    standard: "Norma tecnica",
    standardLower: "norma tecnica",
    thesis: "Tesi",
    thesisLower: "tesi",
    website: "Sito web",
    websiteLower: "sito web",
    youtubeVideo: "Video di YouTube",
    youtubeVideoLower: "video di YouTube",
  },

  // Chrome shared by the five per-style guide routes — /examples, /format,
  // /in-text-citation, /reference-page and /annotated-bibliography, 15 styles each.
  //
  // One section rather than five copies: the CTA, the back link and the cross-links
  // between the five pages are the same sentences on every one of them, and duplicating
  // them per route would be five identical translation units instead of one.
  styleGuidePages: {
    backToStyle: "Torna a {style}",
    backToGenerator: "Torna al generatore di citazioni",
    ctaHeading: "Genera automaticamente citazioni in {style}",
    ctaButton: "Cita ora in {style} →",
    faqHeading: "Domande frequenti",
    resourcesHeading: "Altre risorse",
    relatedHeading: "Altre guide su {style}",
    resourceGenerator: "Generatore di citazioni gratuito ({count} stili)",
    resourceTools: "Strumenti di scrittura gratuiti",
    resourceGuides: "Tutte le guide alle citazioni",
    resourceStyleGenerator: "Generatore di citazioni in {style}",
    resourceExamples: "Esempi di citazioni in {style}",
    resourceFormat: "Guida al formato {style}",
    resourceInText: "Guida alla citazione nel testo in {style}",
    resourceAnnotatedBib: "Guida alla bibliografia commentata in {style}",
    resourceReferencePage: "Guida alla sezione {list} in {style}",
    notFoundTitle: "Non trovato",
  },

  // /examples/<style> — the copy-ready worked examples for all 15 detailed styles.
  examplesPage: {
    metaTitle: "Esempi di citazioni in {style} - Tutti i tipi di fonte [2026]",
    metaDescription:
      "Consulta esempi di citazioni formattate in {style} ({edition}) per libri, articoli di rivista, siti web e altro ancora. Voci per {list} e citazioni nel testo pronte da copiare.",
    ogTitle: "Esempi di citazioni in {style}",
    ogDescription:
      "Voci in {style} pronte da copiare per ogni tipo di fonte comune.",
    ogImageTitle: "Esempi di citazioni in {style}",
    ogKind: "{style} {edition}",
    keywordExamples: "esempi di citazioni in {style}",
    keywordReference: "esempi di riferimenti in {style}",
    keywordInText: "esempi di citazione nel testo in {style}",
    keywordList: "esempi di {listLower} in {style}",
    keywordHowTo: "come citare in {style}",
    breadcrumb: "Esempi di citazioni in {style}",
    heading: "Esempi di citazioni in {style}",
    lead: "Consulta esempi di citazioni correttamente formattate in {style} ({edition}) per {count} tipi di fonte comuni. Ogni esempio include sia la voce per {listLower} sia la citazione nel testo corrispondente.",
    entryLabel: "Voce per {list}:",
    inTextLabel: "Citazione nel testo:",
    ctaBody:
      "Salta la formattazione manuale. Incolla un DOI, un URL o un ISBN in CitationEasy e ottieni una citazione in {style} perfettamente formattata in pochi secondi.",
    faqLookQ: "Che aspetto ha una citazione in {style}?",
    faqLookA:
      "Una citazione in {style} include una voce per {listLower} con i dettagli completi della fonte e una citazione nel testo. Il formato varia in base al tipo di fonte. Ad esempio, una citazione di un libro in {style} ({edition}) include autore, anno, titolo ed editore.",
    faqHowManyQ: "Quanti esempi di citazioni in {style} sono disponibili?",
    faqHowManyA:
      "CitationEasy fornisce esempi di citazioni in {style} per {count} tipi di fonte, tra cui {types}. Ogni esempio include sia la voce per {listLower} sia la citazione nel testo corrispondente.",
    faqCopyQ: "Posso copiare questi esempi di citazioni in {style}?",
    faqCopyA:
      "Sì, gli esempi in {style} in questa pagina mostrano la formattazione esatta per ogni tipo di fonte. Puoi usarli come modelli per le tue citazioni. Per una formattazione automatica, usa il nostro generatore gratuito di citazioni in {style}.",
  },

  // /format/<style> — font, margins, spacing, title page and headers. The VALUES those
  // labels take are per-style prose and live in the overlay, not here.
  formatPage: {
    metaTitle:
      "Guida al formato {style} - Regole di formattazione dell'elaborato [2026]",
    metaDescription:
      "Guida completa alla formattazione dell'elaborato in {style} ({edition}). Scopri il carattere corretto, i margini, l'interlinea, il frontespizio, le intestazioni e le regole di formattazione per {listLower}.",
    ogTitle: "Formato dell'elaborato in {style}",
    ogDescription:
      "Margini, carattere, interlinea e frontespizio per un elaborato in {style}.",
    ogImageTitle: "Formato dell'elaborato in {style}",
    ogImageSubtitle: "Margini {margins} · {spacing}",
    ogKind: "{style} {edition}",
    keywordFormat: "formato {style}",
    keywordPaperFormat: "formato elaborato {style}",
    keywordRules: "regole di formattazione {style}",
    keywordMargins: "margini carattere interlinea {style}",
    keywordList: "formato {listLower} {style}",
    breadcrumb: "Guida al formato {style}",
    heading: "Guida al formato {style}",
    lead: "Segui queste regole di formattazione dell'elaborato in {style} ({edition}) per assicurarti che il tuo documento soddisfi gli standard richiesti. Questa guida copre carattere, margini, interlinea, frontespizio, intestazioni e la formattazione per {listLower}.",
    paperHeading: "Formattazione dell'elaborato",
    paperIntro:
      "Di seguito i requisiti fondamentali di formattazione per gli elaborati scritti in stile {style} ({edition}).",
    labelFont: "Carattere",
    labelFontSize: "Dimensione del carattere",
    labelMargins: "Margini",
    labelSpacing: "Interlinea",
    labelTitlePage: "Frontespizio",
    labelHeaders: "Intestazioni / Numeri di pagina",
    listHeading: "Formato di {list}",
    listIntro:
      "Segui queste regole per formattare correttamente {listLower} nello stile {style}:",
    ctaBody:
      "Lascia che sia CitationEasy a occuparsi della formattazione. Incolla un DOI, un URL o un ISBN e ottieni una citazione in {style} perfettamente formattata in pochi secondi.",
    faqFontQ:
      "Quale carattere e quale dimensione devo usare per il formato {style}?",
    faqFontA:
      "{style} ({edition}) richiede {font}. La dimensione standard del carattere è {fontSize}.",
    faqMarginsQ: "Quali sono i requisiti di margine per {style}?",
    faqMarginsA:
      "Gli elaborati in {style} richiedono {margins}. Questo vale per i margini superiore, inferiore, sinistro e destro di ogni pagina.",
    faqTitlePageQ: "{style} richiede un frontespizio?",
  },

  // /in-text-citation/<style> — the six in-text forms. The forms themselves
  // ("(Smith, 2024)") are per-style prose in the overlay.
  inTextCitationPage: {
    metaTitle:
      "Guida alla citazione nel testo in {style} - Regole ed esempi [2026]",
    metaDescription:
      "Scopri come formattare le citazioni nel testo in {style} ({edition}). Regole per autore singolo, più autori, citazioni dirette, assenza di autore e assenza di data, con esempi.",
    ogTitle: "Citazioni nel testo in {style}",
    ogDescription: "Ogni forma di citazione nel testo in {style}, con esempi.",
    ogImageTitle: "Citazioni nel testo in {style}",
    ogImageSubtitle: "Un autore {singleAuthor} · tre o più {threeOrMore}",
    ogKind: "{style} {edition}",
    keywordInText: "citazione nel testo in {style}",
    keywordExamples: "esempi di citazione nel testo in {style}",
    keywordParenthetical: "citazione tra parentesi in {style}",
    keywordRules: "regole di citazione {style}",
    keywordHowTo: "come citare nel testo in {style}",
    breadcrumb: "Citazioni nel testo in {style}",
    heading: "Citazioni nel testo in {style}",
    lead: "Padroneggia le citazioni nel testo in {style} ({edition}) con questa guida completa. Impara il formato corretto per ogni scenario, inclusi autore singolo e più autori, citazioni dirette e fonti con informazioni mancanti.",
    rulesHeading: "Regole per le citazioni nel testo",
    rulesIntro:
      "Di seguito le regole di citazione nel testo in {style} per ogni scenario comune che incontrerai scrivendo il tuo elaborato.",
    labelSingleAuthor: "Autore singolo",
    labelTwoAuthors: "Due autori",
    labelThreeOrMore: "Tre o più autori",
    labelDirectQuote: "Citazione diretta",
    labelNoAuthor: "Nessun autore",
    labelNoDate: "Nessuna data",
    examplesHeading: "Esempi rapidi",
    examplesIntro:
      "Ecco alcuni esempi reali di citazione nel testo in {style} per diversi tipi di fonte:",
    ctaBody:
      "Smetti di preoccuparti della formattazione delle citazioni nel testo. Incolla un DOI, un URL o un ISBN in CitationEasy e ottieni all'istante sia la voce per {listLower} sia la citazione nel testo.",
    faqFormatQ: "Come si formatta una citazione nel testo in {style}?",
    faqFormatA:
      "Per un autore singolo in {style}, usa il formato {singleAuthor}. Per due autori, usa {twoAuthors}. Per tre o più autori, usa {threeOrMore}.",
    faqQuoteQ: "Come si cita una citazione diretta in {style}?",
    faqQuoteA:
      "Per le citazioni dirette in {style} ({edition}), usa il formato: {directQuote}.",
    faqMissingQ: "Cosa fare se manca l'autore o la data in {style}?",
    faqMissingA:
      "Quando manca l'autore in {style}, usa: {noAuthor}. Quando manca la data, usa: {noDate}.",
  },

  // /reference-page/<style> — how to build the list at the back of the paper.
  //
  // The five `mistake*` strings carry `**bold**` inline markup because the lead-in of
  // each bullet is emphasised inside the sentence; they render through `InlineText`.
  referenceListPage: {
    metaTitle:
      "Guida alla sezione {list} in {style} - Regole di formattazione [2026]",
    metaDescription:
      "Scopri come formattare {listLower} in {style} ({edition}). Regole di formattazione passo dopo passo, esempi ed errori comuni da evitare per {fullName}.",
    ogTitle: "{listLower} in {style}",
    ogDescription: "Come costruire {list} in {style}.",
    ogImageTitle: "{list} in {style}",
    ogKind: "{style} {edition}",
    breadcrumb: "Come formattare {list} in {style}",
    heading: "Come formattare {list} in {style}",
    lead: "La sezione {listLower} è una delle parti più importanti di qualsiasi elaborato accademico formattato in {style} ({edition}). Fornisce ai lettori tutti i dettagli di ogni fonte citata, permettendo loro di individuare e verificare i tuoi riferimenti. Formattarla correttamente è essenziale per la credibilità accademica. Questa guida illustra le regole precise, mostra esempi reali ed evidenzia gli errori più comuni commessi dagli studenti.",
    rulesHeading: "Regole di formattazione",
    rulesIntro: "Segui queste regole quando crei {listLower} in {style}:",
    exampleHeading: "Esempio di {list}",
    exampleIntro:
      "Ecco come si presenta la sezione {listLower} in {style}, con voci per diversi tipi di fonte:",
    mistakesHeading: "Errori comuni da evitare",
    mistakesIntro:
      "Ecco i cinque errori di formattazione più frequenti commessi dagli studenti nella creazione di {listLower} in {style}:",
    mistakeIndent:
      "**Rientro sporgente mancante o errato:** ogni voce deve avere la prima riga allineata a sinistra e tutte le righe successive rientrate di 0,5 pollici. Usa le impostazioni di paragrafo del tuo elaboratore di testi invece di premere il tasto Tab.",
    mistakeOrder:
      '**Ordine alfabetico o sequenza errati:** le voci devono essere disposte secondo le regole di {style}. Ignora gli articoli come "A," "An" e "The" nell\'ordinamento alfabetico. Per gli stili numerici, elenca le voci nell\'ordine in cui compaiono per la prima volta nel testo.',
    mistakeSpacing:
      "**Interlinea incoerente:** ricontrolla l'interlinea in tutta la sezione {listLower}. Mescolare interlinea singola e doppia è un errore comune, facile da non notare.",
    mistakeItalics:
      "**Uso scorretto del corsivo:** metti in corsivo i titoli di opere autonome come libri e riviste, ma non i titoli di articoli o capitoli. Controlla le linee guida di {style} per sapere esattamente quali elementi vanno in corsivo.",
    mistakeLinks:
      "**DOI e URL mancanti o non funzionanti:** includi sempre un DOI quando disponibile, formattato come link cliccabile. Verifica ogni URL prima di consegnare il tuo elaborato, per assicurarti che porti alla fonte corretta.",
    ctaHeading: "Genera automaticamente {list}",
    ctaBody:
      "Salta la formattazione manuale. Incolla un DOI, un URL o un ISBN in CitationEasy e ottieni una citazione in {style} perfettamente formattata in pochi secondi, pronta per {listLower}.",
    faqWhatQ: "Cos'è {list} in {style}?",
    faqWhatA:
      "In {style}, il termine {list} indica la sezione alla fine dell'elaborato che elenca tutte le fonti citate nel testo, formattata secondo le linee guida di {fullName} ({edition}). Permette ai lettori di individuare e verificare ogni fonte citata.",
    faqFormatQ: "Come si formatta la sezione {listLower} in {style}?",
    faqFormatA:
      "Inizia in una nuova pagina, intitolala «{list}» ed elenca tutte le fonti citate nel tuo elaborato. Segui le regole specifiche di {style} per rientro sporgente, interlinea, ordine alfabetico o numerico e uso del corsivo.",
    faqDifferenceQ: "Qual è la differenza tra {listLower} e una bibliografia?",
    faqDifferenceA:
      "La sezione {listLower} include solo le fonti citate direttamente nel tuo elaborato, mentre una bibliografia può includere anche le fonti consultate ma non citate. In {style} ({edition}), l'elenco alla fine dell'elaborato si chiama «{list}».",
  },

  // /annotated-bibliography/<style>. `whatTypes` carries `**bold**` inline markup.
  //
  // `exampleAnnotation` is a specimen annotation, not a citation: it is ordinary prose
  // about an imaginary source and translates like any other paragraph. The reference
  // entry printed above it does not — that comes from `DETAILED_STYLES` and stays as
  // the style wrote it.
  annotatedBibPage: {
    metaTitle: "Guida alla bibliografia commentata in {style} ed esempi [2026]",
    metaDescription:
      "Scopri come scrivere una bibliografia commentata in {style} ({edition}). Include regole di formattazione, voci di esempio e consigli per le bibliografie commentate in {fullName}.",
    ogTitle: "Bibliografia commentata in {style}",
    ogDescription: "Come formattare una bibliografia commentata in {style}.",
    ogImageTitle: "Bibliografia commentata in {style}",
    ogKind: "{style} {edition}",
    breadcrumb: "Bibliografia commentata in {style}",
    heading: "Bibliografia commentata in {style}",
    lead: "Una bibliografia commentata è un elenco di citazioni di fonti, ciascuna corredata da un breve paragrafo descrittivo o valutativo. Nel formato {style} ({edition}), ogni voce inizia con un riferimento correttamente formattato, seguito da un commento che riassume, valuta o riflette sulla fonte. Questa guida ti accompagna attraverso le regole, la formattazione e gli esempi necessari per creare una bibliografia commentata in {style} impeccabile.",
    whatHeading: "Cos'è una bibliografia commentata?",
    whatBody:
      "Una bibliografia commentata va oltre la normale sezione {listLower}, aggiungendo un breve paragrafo dopo ogni citazione. Questo commento offre ai lettori una visione del contenuto, della qualità e della rilevanza della fonte. I docenti assegnano spesso bibliografie commentate per aiutare gli studenti a valutare criticamente le proprie fonti e a dimostrare come ciascuna fonte contribuisca al proprio argomento di ricerca.",
    whatTypes:
      "Esistono tre tipi comuni di commenti: i commenti **descrittivi**, che riassumono la fonte, i commenti **valutativi**, che ne valutano la qualità e l'affidabilità, e i commenti **combinati**, che fanno entrambe le cose. Le istruzioni del tuo compito dovrebbero specificare quale tipo utilizzare.",
    rulesHeading: "Regole per la bibliografia commentata in {style}",
    rulesIntro:
      "Segui queste regole quando formatti una bibliografia commentata in {fullName}:",
    exampleHeading: "Esempio di voce di bibliografia commentata",
    exampleIntro:
      "Di seguito un esempio di voce di bibliografia commentata in {style} per una fonte di tipo {sourceType}:",
    exampleAnnotation:
      "Questa fonte offre una panoramica completa dell'argomento, presentando argomentazioni ben supportate da prove empiriche. L'autore attinge a un'ampia gamma di fonti primarie e secondarie per costruire un caso persuasivo. La metodologia è rigorosa e i risultati sono presentati con chiarezza, rendendo questo lavoro una risorsa preziosa per comprendere l'argomento. Questa fonte è particolarmente utile per definire il quadro teorico del progetto di ricerca e offre prospettive che completano le altre opere della bibliografia.",
    tipsHeading: "Consigli per scrivere commenti in {style}",
    tipSummarize:
      "Inizia riassumendo la fonte in due o tre frasi, poi aggiungi la tua valutazione dei suoi punti di forza, dei limiti e della rilevanza.",
    tipTone:
      "Usa la terza persona e un tono accademico in tutti i tuoi commenti. Evita un linguaggio colloquiale o riflessioni in prima persona, a meno che il tuo docente non le richieda.",
    tipLength:
      "Mantieni ogni commento tra le 100 e le 200 parole. Sii conciso e concentrati sugli aspetti più importanti della fonte.",
    tipRelevance:
      "Spiega come la fonte si inserisce nella tua ricerca complessiva. Descrivi quale prospettiva o prova unica apporta.",
    tipCitation:
      "Assicurati che la parte di citazione di ogni voce segua esattamente le regole di formattazione di {fullName} prima di scrivere il commento.",
    ctaHeading: "Crea automaticamente la tua bibliografia commentata",
    ctaBody:
      "Usa il generatore di bibliografie commentate di CitationEasy per generare citazioni in {style} correttamente formattate, poi aggiungi i tuoi commenti.",
    ctaButton: "Apri il generatore di bibliografie commentate →",
    faqWhatQ: "Cos'è una bibliografia commentata in {style}?",
    faqWhatA:
      "Una bibliografia commentata in {style} ({edition}) è un elenco di citazioni formattate secondo lo stile {fullName}, ciascuna seguita da un breve paragrafo che riassume, valuta o riflette sulla fonte. Aiuta i lettori a comprendere la rilevanza e la qualità di ciascuna fonte.",
    faqLengthQ: "Quanto devono essere lunghi i commenti?",
    faqLengthA:
      "I commenti sono in genere lunghi tra le 100 e le 200 parole. Alcuni compiti possono richiedere commenti più brevi o più lunghi, a seconda della profondità di analisi attesa. Controlla sempre le indicazioni del tuo docente per i requisiti specifici di lunghezza.",
    faqIncludeQ: "Cosa dovrebbe includere un commento?",
    faqIncludeA:
      "Un commento dovrebbe includere un breve riassunto dell'argomentazione principale o dei risultati della fonte, una valutazione della sua credibilità e utilità, e una riflessione su come la fonte si inserisce nella tua ricerca. Alcuni commenti sono puramente descrittivi, mentre altri includono un'analisi critica.",
  },

  guidePage: {
    byline: "Di {author}",
    published: "Pubblicato il {date}",
    updated: "Aggiornato il {date}",
    readingTime:
      "one:{count} minuto di lettura|other:{count} minuti di lettura",
    wordCount: "one:{count} parola|other:{count} parole",
  },

  // /guides hub, /guides/<category> index pages, and the chrome around each guide
  guideLibrary: {
    eyebrow: "Biblioteca delle guide",
    browseByCategory: "Sfoglia per categoria",
    recentlyUpdated: "Aggiornate di recente",
    allGuides: "Tutte le guide",
    guideCount: "one:{count} guida|other:{count} guide",
    updatedOn: "Aggiornata il {date}",
    minRead: "one:{count} min di lettura|other:{count} min di lettura",
    onThisPage: "In questa pagina",
    viewAllInCategory: "Tutte le guide {category}",
    moreInCategory: "Altro in {category}",
    aboutThisGuide: "Su questa guida",
    provenance:
      "Scritta da {author}. Pubblicata il {published}; ultimo aggiornamento il {updated}.",
    categoryIntroSuffix:
      "Ogni guida è gratuita, scritta per studenti e ricercatori e datata, così sai quando è stata controllata l'ultima volta.",
    photoCredit: "Foto: {author}, {source} ({license})",
    imageAlt: "Fotografia che illustra le guide {category}",
    imageSourceCommons: "Wikimedia Commons",
    apaStyleName: "Stile APA",
    apaStyleDescription:
      "Come citare siti web, articoli di rivista, libri, video e altro in APA 7ª edizione, con esempi di bibliografia e citazioni nel testo.",
    mlaStyleName: "Stile MLA",
    mlaStyleDescription:
      "Come citare ogni fonte comune in MLA 9ª edizione, dalle pagine web e articoli a poesie, opere teatrali, film e materiali didattici.",
    chicagoStyleName: "Stile Chicago",
    chicagoStyleDescription:
      "Guide al Chicago Manual of Style con citazioni note-bibliografia e autore-data per libri, articoli, archivi, documenti governativi e media.",
    styleComparisonsName: "Confronti tra stili",
    styleComparisonsDescription:
      "Confronti affiancati di APA, MLA, Chicago, Harvard, Vancouver, IEEE e altri, con consigli sullo stile richiesto dalla tua disciplina.",
    researchMethodsName: "Metodi di ricerca",
    researchMethodsDescription:
      "Come pianificare una tesina, fare una rassegna della letteratura, prendere appunti, valutare le fonti, evitare il plagio e costruire una bibliografia.",
    academicWritingName: "Scrittura accademica",
    academicWritingDescription:
      "Guide alla scrittura di abstract, introduzioni, conclusioni, tesi, sezioni di metodologia e prosa accademica chiara.",
    fieldSpecificName: "Per disciplina",
    fieldSpecificDescription:
      "Guide alla citazione per singole discipline: psicologia, storia, letteratura, medicina, ingegneria, diritto, infermieristica e altre.",
    sourceEvaluationName: "Valutazione delle fonti",
    sourceEvaluationDescription:
      "Come giudicare la credibilità, distinguere fonti accademiche e divulgative, usare Google Scholar e le banche dati e riconoscere i pregiudizi.",
    citationHelpName: "Aiuto per le citazioni",
    citationHelpDescription:
      "Risposte ai casi difficili: autore o data mancanti, più autori, PDF, strumenti di IA, casi legali e importazione o esportazione delle citazioni.",
  },

  common: {
    backToGuides: "← Torna alle guide",
    free: "Gratuito",
    noSignup: "Senza registrazione",
    backToHome: "Torna al generatore di citazioni",
    inEnglish: "In inglese",
    viewInEnglish: "Vedi in inglese",
    availableInEnglish:
      "Questa pagina è disponibile in inglese con maggiori dettagli.",
    otherLanguages: "Altre lingue",
    skipToContent: "Vai al contenuto",
    cookieNotice:
      "Usiamo cookie per analisi e pubblicità per mantenere CitationEasy gratuito.",
    privacyPolicy: "Informativa sulla privacy",
    accept: "Accetta",
    decline: "Rifiuta",
    advertisement: "Pubblicità",
  },
  legalPage: {
    backLink: "← Torna al generatore",
    effective: "Data di entrata in vigore:",
    updated: "Ultimo aggiornamento:",
  },
};

export default it;
